<template>
  <div class="user-detail-container" v-loading="loading">
    <div class="control-panel">
      <div class="control-container">
        <div class="header-info">
          <i class="el-icon-user"></i>
          <span>{{ $t('userManagement.userDetail') }}</span>
        </div>
        <div class="action-wrapper">
          <el-button @click="handleBack" size="small">
            <i class="el-icon-back"></i> {{ $t('common.back') }}
          </el-button>
        </div>
      </div>
    </div>
    
    <div v-if="!loading && user.userid">
      <!-- 顶部概览卡片 -->
      <el-card class="user-overview-card" shadow="hover" v-loading="loading">
        <div class="user-overview-content">
          <div class="user-avatar-wrapper">
            <el-avatar :size="80" :src="getFullUrl(user.avatar)">
              {{ user.name ? user.name.charAt(0).toUpperCase() : user.username.charAt(0).toUpperCase() }}
            </el-avatar>
          </div>
          <div class="user-main-info">
            <div class="user-name-row">
              <span class="name-text">{{ user.name || user.username }}</span>
              <el-tag :type="getRoleTagType" size="small" effect="dark" class="role-tag">
                {{ userRoleName || $t('userManagement.user') }}
              </el-tag>
              <el-tag :color="getLevelColor(user.userLevel)" size="small" effect="dark" class="level-tag" style="margin-left: 8px; color: #fff; border: none;">
                {{ getLevelDisplayText(user.userLevel) }}
              </el-tag>
              <el-tag :type="user.status === 0 ? 'info' : user.status === 2 ? 'warning' : 'success'" size="small" effect="dark" class="status-tag">
                {{ user.status === 0 ? $t('common.cancelled') : user.status === 2 ? $t('common.frozen') : $t('common.inUse') }}
              </el-tag>
            </div>
            <div class="user-sub-info">
              <span class="username">@{{ user.username }}</span>
              <span class="divider">|</span>
              <span class="userid">
                ID: {{ user.userid }}
                <el-tooltip :content="$t('common.copy')" placement="top">
                  <i class="el-icon-document-copy copy-icon" @click="handleCopy(user.userid)"></i>
                </el-tooltip>
              </span>
            </div>
          </div>
          <div class="user-actions">
            <el-button type="warning" @click="handleEditUser" :disabled="user.username === 'admin' || loading" size="medium" v-if="hasPermission('sys:user:update') && user.status !== 0">
              <i class="el-icon-edit"></i> {{ $t('common.edit') }}
            </el-button>
          </div>
        </div>
      </el-card>

      <el-row :gutter="20" class="info-grid display-flex">
        <el-col :xs="24" :sm="24" :md="16" class="flex-col">
          <el-card class="detail-card info-card h-full" shadow="hover">
            <div slot="header" class="card-header">
              <span><i class="el-icon-info"></i> {{ $t('userManagement.userBasicInfo') }}</span>
            </div>
            <el-descriptions :column="2" border>
              <el-descriptions-item :label="$t('userManagement.username')">
                <template slot="label">
                  <i class="el-icon-user"></i> {{ $t('userManagement.username') }}
                </template>
                <span class="highlight-text">{{ user.username }}</span>
              </el-descriptions-item>
              <el-descriptions-item :label="$t('userManagement.name')">
                <template slot="label">
                  <i class="el-icon-user"></i> {{ $t('userManagement.name') }}
                </template>
                <span class="highlight-text">{{ user.name || user.username }}</span>
              </el-descriptions-item>
              <el-descriptions-item :label="$t('userManagement.gender')">
                <template slot="label">
                  <i class="el-icon-info"></i> {{ $t('userManagement.gender') }}
                </template>
                <template v-if="user.gender === 1">
                  <i class="el-icon-male" style="color: #409eff"></i> {{ $t('userManagement.male') }}
                </template>
                <template v-else-if="user.gender === 2">
                  <i class="el-icon-female" style="color: #f56c6c"></i> {{ $t('userManagement.female') }}
                </template>
                <template v-else>
                  <span class="text-secondary">{{ $t('userManagement.unknown') }}</span>
                </template>
              </el-descriptions-item>
              <el-descriptions-item :label="$t('userManagement.email')">
                <template slot="label">
                  <i class="el-icon-message"></i> {{ $t('userManagement.email') }}
                </template>
                <a v-if="user.email" :href="'mailto:' + user.email" class="link-text">{{ user.email }}</a>
                <span v-else class="text-secondary">-</span>
              </el-descriptions-item>
              <el-descriptions-item :label="$t('userManagement.phone')">
                <template slot="label">
                  <i class="el-icon-phone-outline"></i> {{ $t('userManagement.phone') }}
                </template>
                <span v-if="user.mobile" class="highlight-text">{{ user.mobile }}</span>
                <span v-else class="text-secondary">-</span>
              </el-descriptions-item>
              <el-descriptions-item :label="$t('userManagement.role')">
                <template slot="label">
                  <i class="el-icon-medal"></i> {{ $t('userManagement.role') }}
                </template>
                <div class="tag-group">
                  <el-tag 
                    v-if="userRoleName"
                    size="small"
                    :type="getRoleTagType"
                    effect="plain"
                    class="role-tag-item"
                  >
                    {{ userRoleName }}
                  </el-tag>
                  <span v-else class="text-secondary">-</span>
                </div>
              </el-descriptions-item>
              <el-descriptions-item :label="$t('userManagement.userGroups')">
                <template slot="label">
                  <i class="el-icon-folder"></i> {{ $t('userManagement.userGroups') }}
                </template>
                <div class="tag-group">
                  <el-tag 
                    v-for="group in userGroups" 
                    :key="group.id"
                    size="small"
                    type="info"
                    effect="plain"
                    class="group-tag-item"
                  >
                    {{ group.name }}
                  </el-tag>
                  <span v-if="userGroups.length === 0" class="text-secondary">-</span>
                </div>
              </el-descriptions-item>
              <el-descriptions-item :label="$t('userManagement.userLevel')">
                <template slot="label">
                  <i class="el-icon-medal"></i> {{ $t('userManagement.userLevel') }}
                </template>
                <el-tag :color="getLevelColor(user.userLevel)" size="small" effect="dark" style="color: #fff; border: none;">
                  {{ getLevelDisplayText(user.userLevel) }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item :label="$t('userManagement.accountBalance')">
                <template slot="label">
                  <i class="el-icon-wallet"></i> {{ $t('userManagement.accountBalance') }}
                </template>
                <span class="highlight-text" style="color: #f56c6c; font-weight: bold;">￥{{ formatBalance(user.accountBalance) }}</span>
              </el-descriptions-item>
              <el-descriptions-item :label="$t('userManagement.points')">
                <template slot="label">
                  <i class="el-icon-coin"></i> {{ $t('userManagement.points') }}
                </template>
                <div style="display: flex; align-items: center; gap: 8px;">
                  <el-tag size="small" effect="plain" type="warning">{{ user.points || 0 }}</el-tag>
                  <el-button type="text" size="mini" icon="el-icon-document" @click="handleViewPointsRecords">积分明细</el-button>
                </div>
              </el-descriptions-item>
              <el-descriptions-item :label="$t('common.createdAt')">
                <template slot="label">
                  <i class="el-icon-time"></i> {{ $t('common.createdAt') }}
                </template>
                <span class="text-secondary">{{ formatDate(user.createDate || user.created_at) }}</span>
              </el-descriptions-item>
              <el-descriptions-item :label="$t('common.status')" :span="2">
                <template slot="label">
                  <i class="el-icon-setting"></i> {{ $t('common.status') }}
                </template>
                <el-tag :type="user.status === 0 ? 'info' : user.status === 2 ? 'warning' : 'success'" size="small" effect="dark">
                  {{ user.status === 0 ? $t('common.cancelled') : user.status === 2 ? $t('common.frozen') : $t('common.inUse') }}
                </el-tag>
              </el-descriptions-item>
            </el-descriptions>
          </el-card>
        </el-col>
        
        <el-col :xs="24" :sm="24" :md="8" class="flex-col">
          <el-card class="detail-card stat-card h-full" shadow="hover">
            <div slot="header" class="card-header">
              <span><i class="el-icon-pie-chart"></i> {{ $t('userManagement.dataOverview') }}</span>
            </div>
            <div class="stat-items">
              <div class="stat-item devices" @click="scrollToSection('devices')">
                <div class="stat-icon-wrapper"><i class="el-icon-mobile-phone"></i></div>
                <div class="stat-info">
                  <div class="stat-value">{{ userDevices.length }}</div>
                  <div class="stat-label">{{ $t('userManagement.deviceCount') }}</div>
                </div>
              </div>
              <div class="stat-item peripherals" @click="scrollToSection('peripherals')">
                <div class="stat-icon-wrapper"><i class="el-icon-postcard"></i></div>
                <div class="stat-info">
                  <div class="stat-value">{{ userPeripherals.length }}</div>
                  <div class="stat-label">{{ $t('userManagement.peripheralCount') }}</div>
                </div>
              </div>
              <div class="stat-item agents" @click="scrollToSection('agents')">
                <div class="stat-icon-wrapper"><i class="el-icon-cpu"></i></div>
                <div class="stat-info">
                  <div class="stat-value">{{ userAgents.length }}</div>
                  <div class="stat-label">{{ $t('agent.agentCount') }}</div>
                </div>
              </div>
              <div class="stat-item collections" @click="scrollToSection('collections')">
                <div class="stat-icon-wrapper"><i class="el-icon-picture-outline"></i></div>
                <div class="stat-info">
                  <div class="stat-value">{{ digitalCollections.length }}</div>
                  <div class="stat-label">数字藏品</div>
                </div>
              </div>
              <div class="stat-item memorials" @click="scrollToSection('memorials')">
                <div class="stat-icon-wrapper"><i class="el-icon-present"></i></div>
                <div class="stat-info">
                  <div class="stat-value">{{ userMemorials.length }}</div>
                  <div class="stat-label">{{ $t('userManagement.memorialCount') }}</div>
                </div>
              </div>
              <div class="stat-item rights" @click="scrollToSection('rights')">
                <div class="stat-icon-wrapper"><i class="el-icon-shopping-bag-1"></i></div>
                <div class="stat-info">
                  <div class="stat-value">{{ userRights.length }}</div>
                  <div class="stat-label">权益兑换</div>
                </div>
              </div>
              <div class="stat-item groups" @click="scrollToSection('groups')">
                <div class="stat-icon-wrapper"><i class="el-icon-menu"></i></div>
                <div class="stat-info">
                  <div class="stat-value">{{ userGroups.length }}</div>
                  <div class="stat-label">{{ $t('userManagement.groupCount') }}</div>
                </div>
              </div>
              <div class="stat-item logs" @click="scrollToSection('logs')">
                <div class="stat-icon-wrapper"><i class="el-icon-document"></i></div>
                <div class="stat-info">
                  <div class="stat-value">{{ userLogTotal }}</div>
                  <div class="stat-label">{{ $t('userManagement.userOperationLogs') }}</div>
                </div>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>

      <!-- 用户设备信息 -->
      <el-card id="devices" class="user-detail-devices detail-card" shadow="hover">
        <template slot="header">
          <div class="card-header">
            <span><i class="el-icon-mobile-phone"></i> {{ $t('userManagement.userDevices') }}</span>
            <div class="header-actions">
              <el-button 
                type="text" 
                icon="el-icon-refresh" 
                :loading="loadingDevices" 
                @click="loadRelatedData"
                style="margin-right: 15px;"
              >
                {{ $t('common.refresh') }}
              </el-button>
              <el-input
                v-model="deviceSearch"
                :placeholder="$t('userManagement.searchDevice')"
                clearable
                style="width: 240px; margin-right: 10px;"
                @input="handleDeviceSearch"
              >
                <i slot="prefix" class="el-input__icon el-icon-search"></i>
              </el-input>
              <el-select 
                v-model="deviceTypeFilter" 
                clearable 
                :placeholder="$t('userManagement.filterByType')"
                style="width: 180px; margin-right: 10px;"
                @change="handleDeviceSearch"
              >
                <i slot="prefix" class="el-input__icon el-icon-cpu"></i>
                <el-option
                  v-for="item in firmwareTypes"
                  :key="item.key"
                  :label="item.name"
                  :value="item.key"
                ></el-option>
              </el-select>
              <el-button type="primary" size="small" icon="el-icon-plus" @click="handleBindDevice" v-if="hasPermission('sys:device:update')">
                {{ $t('userManagement.bindDevice') }}
              </el-button>
            </div>
          </div>
        </template>
        
        <el-table 
          v-loading="loadingDevices"
          :data="paginatedDevices" 
          stripe 
          height="400"
          style="width: 100%"
          :empty-text="$t('userManagement.noDevices')"
        >
          <el-table-column prop="device_name" :label="$t('device.deviceName')" min-width="140" align="center">
            <template slot-scope="scope">
              <span class="highlight-text">{{ scope.row.device_name || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="model" :label="$t('userManagement.deviceType')" width="100" align="center">
            <template slot-scope="scope">
              {{ getDeviceModelLabel(scope.row.model) }}
            </template>
          </el-table-column>
          <el-table-column prop="app_version" :label="$t('device.firmwareVersion')" width="120" align="center">
            <template slot-scope="scope">
              <el-tag size="mini" type="info" effect="plain" v-if="scope.row.app_version" style="border-radius: 10px;">
                {{ scope.row.app_version }}
              </el-tag>
              <span v-else class="text-secondary">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="device_remark" :label="$t('device.deviceRemark')" min-width="180" align="left">
            <template slot-scope="scope">
              <el-input
                v-model="scope.row.device_remark"
                size="mini"
                :placeholder="$t('device.enterRemarks')"
                @blur="handleRemarkBlur(scope.row)"
                clearable
                :disabled="!hasPermission('sys:device:update')"
              >
              </el-input>
            </template>
          </el-table-column>
          <el-table-column prop="auto_update" :label="$t('device.autoUpdate')" width="100" align="center">
            <template slot-scope="scope">
              <el-switch
                v-model="scope.row.auto_update"
                :active-value="1"
                :inactive-value="0"
                @change="(val) => handleAutoUpdateChange(scope.row, val)"
                :disabled="!hasPermission('sys:device:update')"
              >
              </el-switch>
            </template>
          </el-table-column>
          <el-table-column prop="status" :label="$t('common.status')" width="100" align="center">
            <template slot-scope="scope">
              <el-tag :type="scope.row.status === 'online' ? 'success' : 'danger'" size="small" effect="dark" style="border-radius: 10px; border: none;">
                {{ scope.row.status === 'online' ? $t('common.online') : $t('common.offline') }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="bind_time" :label="$t('userManagement.bindTime')" width="160" align="center">
            <template slot-scope="scope">
              {{ scope.row.bind_time ? formatDate(scope.row.bind_time) : '-' }}
            </template>
          </el-table-column>
          <el-table-column :label="$t('common.operation')" width="220" fixed="right" align="center">
            <template slot-scope="scope">
              <div class="action-buttons">
                <el-button
                  size="mini"
                  type="primary"
                  plain
                  icon="el-icon-view"
                  @click="handleViewDevice(scope.row)"
                  v-if="hasPermission('sys:device:info')"
                >
                  {{ $t('common.detail') }}
                </el-button>
                <el-button
                  size="mini"
                  type="danger"
                  plain
                  icon="el-icon-delete"
                  @click="handleUnbindDevice(scope.row)"
                  :disabled="!(scope.row.user_id || scope.row.userId)"
                  v-if="hasPermission('sys:device:update')"
                >
                  {{ $t('device.unbindDevice') }}
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-container" v-if="filteredDevices.length > 0">
          <el-pagination
            @size-change="handleDeviceSizeChange"
            @current-change="handleDevicePageChange"
            :current-page="devicePage"
            :page-sizes="[5, 10, 20, 50]"
            :page-size="devicePageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="filteredDevices.length"
          >
          </el-pagination>
        </div>
      </el-card>

      <el-card id="peripherals" class="user-detail-peripherals detail-card" shadow="hover">
        <template slot="header">
          <div class="card-header">
            <span><i class="el-icon-postcard"></i> NFC周边</span>
            <div class="header-actions">
              <el-button 
                type="text" 
                icon="el-icon-refresh" 
                :loading="loadingPeripherals" 
                @click="loadRelatedData"
                style="margin-right: 15px;"
              >
                {{ $t('common.refresh') }}
              </el-button>
              <el-input
                v-model="peripheralSearch"
                placeholder="搜索NFC周边"
                clearable
                style="width: 240px; margin-right: 10px;"
                @input="handlePeripheralSearch"
              >
                <i slot="prefix" class="el-input__icon el-icon-search"></i>
              </el-input>
              <el-select 
                v-model="peripheralTypeFilter" 
                clearable 
                placeholder="按卡类型筛选"
                style="width: 180px; margin-right: 10px;"
                @change="handlePeripheralSearch"
              >
                <i slot="prefix" class="el-input__icon el-icon-watch"></i>
                <el-option label="普通卡" value="NORMAL"></el-option>
                <el-option label="通用卡" value="UNIVERSAL"></el-option>
                <el-option label="活动卡" value="ACTIVITY"></el-option>
                <el-option label="限定卡" value="LIMITED"></el-option>
                <el-option label="礼品卡" value="GIFT"></el-option>
              </el-select>
              <el-button type="primary" size="small" icon="el-icon-plus" @click="handleBindPeripheral" v-if="hasPermission('sys:peripheral:update')">
                绑定周边
              </el-button>
            </div>
          </div>
        </template>
        
        <el-table 
          v-loading="loadingPeripherals"
          :data="paginatedPeripherals" 
          stripe 
          height="400"
          style="width: 100%"
          empty-text="暂无NFC周边"
        >
          <el-table-column prop="sku" label="SKU编码" min-width="140" align="center">
            <template slot-scope="scope">
              <span class="highlight-text">{{ scope.row.sku || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="nfcUuid" label="NFC卡号" min-width="160" align="center">
            <template slot-scope="scope">
              <span>{{ scope.row.nfcUuid || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="cardType" label="卡类型" width="120" align="center">
            <template slot-scope="scope">
              <el-tag size="small" :type="getCardTypeTag(scope.row.cardType)" effect="plain">
                {{ getCardTypeLabel(scope.row.cardType) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="deviceCode" label="刷卡设备" width="160" align="center">
            <template slot-scope="scope">
              <span>{{ scope.row.deviceCode || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="bindTime" label="绑定时间" width="160" align="center">
            <template slot-scope="scope">
              {{ scope.row.bindTime ? formatDate(scope.row.bindTime) : '-' }}
            </template>
          </el-table-column>
          <el-table-column :label="$t('common.operation')" width="160" fixed="right" align="center">
            <template slot-scope="scope">
              <div class="action-buttons">
                <el-button
                  size="mini"
                  type="danger"
                  plain
                  icon="el-icon-delete"
                  @click="handleUnbindPeripheral(scope.row)"
                  :disabled="!scope.row.canUnbind"
                  v-if="hasPermission('sys:peripheral:update')"
                >
                  解绑
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-container" v-if="filteredPeripherals.length > 0">
          <el-pagination
            @size-change="handlePeripheralSizeChange"
            @current-change="handlePeripheralPageChange"
            :current-page="peripheralPage"
            :page-sizes="[5, 10, 20, 50]"
            :page-size="peripheralPageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="filteredPeripherals.length"
          >
          </el-pagination>
        </div>
      </el-card>

      <el-card id="agents" class="user-detail-agents detail-card" shadow="hover">
        <template slot="header">
          <div class="card-header">
            <span><i class="el-icon-cpu"></i> {{ $t('userManagement.userAgents') }}</span>
            <div class="header-actions">
              <el-input
                v-model="agentSearch"
                :placeholder="$t('userManagement.searchAgent')"
                clearable
                style="width: 240px;"
                @input="handleAgentSearch"
              >
                <i slot="prefix" class="el-input__icon el-icon-search"></i>
              </el-input>
            </div>
          </div>
        </template>
        
        <el-table 
          v-loading="loadingAgents"
          :data="paginatedAgents" 
          stripe 
          height="400"
          style="width: 100%"
          :empty-text="$t('userManagement.noAgents')"
        >
          <el-table-column prop="agentName" :label="$t('agent.agentName')" min-width="140" align="center">
            <template slot-scope="scope">
              <span class="highlight-text">{{ scope.row.agentName || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="agentCode" :label="$t('agent.agentCode')" width="120" align="center">
            <template slot-scope="scope">
              <span>{{ scope.row.agentCode || '-' }}</span>
              <el-button 
                v-if="scope.row.agentCode"
                type="text" 
                icon="el-icon-document-copy" 
                size="mini" 
                style="margin-left: 5px;"
                @click.stop="handleCopy(scope.row.agentCode)"
              ></el-button>
            </template>
          </el-table-column>
          <el-table-column prop="systemPrompt" :label="$t('agent.systemPrompt')" min-width="200" show-overflow-tooltip align="left"></el-table-column>
          <el-table-column prop="createDate" :label="$t('common.createdAt')" width="160" align="center">
            <template slot-scope="scope">
              {{ scope.row.createDate ? formatDate(scope.row.createDate) : '-' }}
            </template>
          </el-table-column>
          <el-table-column :label="$t('common.operation')" width="300" fixed="right" align="center">
            <template slot-scope="scope">
              <div class="action-buttons">
                <el-button
                  size="mini"
                  type="primary"
                  plain
                  icon="el-icon-view"
                  @click="handleViewAgent(scope.row)"
                >
                  {{ $t('common.detail') }}
                </el-button>
                <el-button
                  size="mini"
                  type="primary"
                  plain
                  icon="el-icon-chat-dot-round"
                  @click="handleViewChatHistory(scope.row)"
                >
                  {{ $t('common.chatHistory') }}
                </el-button>
                <el-button
                  size="mini"
                  type="danger"
                  plain
                  icon="el-icon-delete"
                  @click="handleDeleteAgent(scope.row)"
                >
                  {{ $t('common.delete') }}
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-container" v-if="filteredAgents.length > 0">
          <el-pagination
            @size-change="handleAgentSizeChange"
            @current-change="handleAgentPageChange"
            :current-page="agentPage"
            :page-sizes="[5, 10, 20, 50]"
            :page-size="agentPageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="filteredAgents.length"
          >
          </el-pagination>
        </div>
      </el-card>

      <!-- 数字藏品 -->
      <el-card id="collections" class="user-detail-collections detail-card" shadow="hover">
        <template slot="header">
          <div class="card-header">
            <span><i class="el-icon-picture-outline"></i> 数字藏品</span>
            <div class="header-actions">
              <el-button
                type="text"
                icon="el-icon-refresh"
                :loading="loadingCollections"
                @click="fetchDigitalCollections"
                style="margin-right: 15px;"
              >
                {{ $t('common.refresh') }}
              </el-button>
            </div>
          </div>
        </template>

        <div v-loading="loadingCollections">
          <div v-if="digitalCollections.length === 0" class="memorial-empty">
            <i class="el-icon-picture-outline" style="font-size: 40px; color: #dcdfe6;"></i>
            <p>暂无数字藏品</p>
          </div>
          <div v-else>
            <!-- 系列概览 -->
            <div class="collection-series-overview" v-if="collectionSeriesList.length > 0">
              <div
                v-for="series in collectionSeriesList"
                :key="series.seriesId || 'none'"
                class="collection-series-card"
                :class="{ active: activeSeries === (series.seriesId || 'none') }"
                @click="toggleSeriesFilter(series.seriesId || 'none')"
              >
                <div class="series-cover">
                  <el-image
                    v-if="series.coverImage"
                    :src="getFullUrl(series.coverImage)"
                    fit="cover"
                    class="series-cover-img"
                  />
                  <div v-else class="series-cover-placeholder">
                    <i class="el-icon-collection"></i>
                  </div>
                </div>
                <div class="series-info">
                  <div class="series-name">{{ series.seriesName || '未分类' }}</div>
                  <div class="series-count">{{ series.count }} 件藏品</div>
                </div>
              </div>
            </div>

            <!-- 藏品列表 -->
            <div class="collection-grid">
              <div
                v-for="item in filteredCollections"
                :key="item.id"
                class="collection-item"
              >
                <div class="collection-image-wrapper">
                  <el-image
                    v-if="item.collectionImage"
                    :src="getFullUrl(item.collectionImage)"
                    fit="cover"
                    class="collection-image"
                    :preview-src-list="filteredCollections.filter(c => c.collectionImage).map(c => getFullUrl(c.collectionImage))"
                  />
                  <div v-else class="collection-image-placeholder">
                    <i class="el-icon-picture-outline"></i>
                  </div>
                  <div class="collection-status-badge">
                    <el-tag
                      :type="item.status === 1 ? 'success' : item.status === 2 ? 'warning' : 'info'"
                      size="mini"
                      effect="dark"
                    >
                      {{ item.status === 1 ? '正常' : item.status === 2 ? '已转赠' : '已失效' }}
                    </el-tag>
                  </div>
                </div>
                <div class="collection-detail">
                  <div class="collection-name" :title="item.collectionName">{{ item.collectionName }}</div>
                  <div class="collection-series-name" v-if="item.seriesName">{{ item.seriesName }}</div>
                  <div class="collection-meta">
                    <span class="collection-no" :title="item.collectionNo">{{ item.collectionNo }}</span>
                  </div>
                  <div class="collection-time">
                    <i class="el-icon-time"></i>
                    {{ formatDate(item.acquiredAt) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </el-card>

      <!-- 用户获取的纪念记录 -->
      <el-card id="memorials" class="user-detail-memorials detail-card" shadow="hover">
        <template slot="header">
          <div class="card-header">
            <span><i class="el-icon-present"></i> 纪念记录</span>
            <div class="header-actions">
              <el-button 
                type="text" 
                icon="el-icon-refresh" 
                :loading="loadingMemorials" 
                @click="fetchUserMemorials"
                style="margin-right: 15px;"
              >
                {{ $t('common.refresh') }}
              </el-button>
              <el-button 
                type="primary" 
                size="small" 
                icon="el-icon-plus" 
                @click="openAddMemorialDialog"
                v-if="hasPermission('sys:user:update')"
              >
                添加记录
              </el-button>
            </div>
          </div>
        </template>

        <div v-loading="loadingMemorials">
          <div v-if="userMemorials.length === 0" class="memorial-empty">
            <i class="el-icon-present" style="font-size: 40px; color: #dcdfe6;"></i>
            <p>暂无纪念记录</p>
          </div>
          <div v-else class="memorial-grid">
            <div v-for="item in userMemorials" :key="item.id" class="memorial-card">
              <div class="memorial-card-icon">
                <el-image v-if="item.icon" :src="getFullUrl(item.icon)" fit="contain" style="width: 56px; height: 56px; border-radius: 8px;"></el-image>
                <div v-else class="memorial-icon-placeholder">
                  <i class="el-icon-present"></i>
                </div>
              </div>
              <div class="memorial-card-info">
                <div class="memorial-card-title">{{ item.title || '未知纪念' }}</div>
                <div class="memorial-card-subtitle" v-if="item.subtitle">{{ item.subtitle }}</div>
                <div class="memorial-card-meta">
                  <el-tag v-if="item.memorialType" size="mini" :type="getMemorialTypeTagType(item.memorialType)">{{ getMemorialTypeLabel(item.memorialType) }}</el-tag>
                  <el-tag v-if="item.seriesName" size="mini" type="info" style="margin-left: 4px;">{{ getMemorialSeriesLabel(item.seriesName) }}</el-tag>
                </div>
                <div class="memorial-card-time">
                  <i class="el-icon-time"></i>
                  {{ formatDate(item.acquireTime) }}
                  <span v-if="item.acquireMethod" class="memorial-method"> · {{ getAcquireMethodLabel(item.acquireMethod) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </el-card>

      <!-- 用户权益兑换记录 -->
      <el-card id="rights" class="user-detail-rights detail-card" shadow="hover">
        <template slot="header">
          <div class="card-header">
            <span><i class="el-icon-goods"></i> 权益兑换</span>
            <div class="header-actions">
              <el-button 
                type="text" 
                icon="el-icon-refresh" 
                :loading="loadingRights" 
                @click="fetchUserRights"
                style="margin-right: 15px;"
              >
                {{ $t('common.refresh') }}
              </el-button>
              <el-button 
                type="primary" 
                size="small" 
                icon="el-icon-shopping-cart-2" 
                @click="openExchangeDialog"
                v-if="hasPermission('activity:shop:exchange')"
              >
                积分兑换
              </el-button>
            </div>
          </div>
        </template>

        <div v-loading="loadingRights">
          <div v-if="userRights.length === 0" class="memorial-empty">
            <i class="el-icon-goods" style="font-size: 40px; color: #dcdfe6;"></i>
            <p>暂无兑换记录</p>
          </div>
          <div v-else class="memorial-grid">
            <div v-for="item in userRights" :key="item.id" class="memorial-card">
              <div class="memorial-card-icon">
                <el-image v-if="item.goodsIcon" :src="getFullUrl(item.goodsIcon)" fit="contain" style="width: 56px; height: 56px; border-radius: 8px;"></el-image>
                <div v-else class="memorial-icon-placeholder">
                  <i class="el-icon-goods"></i>
                </div>
              </div>
              <div class="memorial-card-info">
                <div class="memorial-card-title">{{ item.goodsName || '未知权益' }}</div>
                <div class="memorial-card-meta">
                  <el-tag v-if="item.rightsType" size="mini" :type="getRightsTypeTagType(item.rightsType)">{{ getRightsTypeLabel(item.rightsType) }}</el-tag>
                  <el-tag size="mini" :type="getRightsStatusType(item.status)" style="margin-left: 4px;">{{ getRightsStatusLabel(item.status) }}</el-tag>
                </div>
                <div class="memorial-card-time">
                  <i class="el-icon-time"></i>
                  {{ formatDate(item.exchangeTime) }}
                  <span class="memorial-method"> · {{ item.pointsCost }}积分</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </el-card>

      <!-- 添加纪念记录弹窗 -->
      <el-dialog title="添加纪念记录" :visible.sync="addMemorialDialogVisible" width="720px" :close-on-click-modal="false" append-to-body>
        <div style="margin-bottom: 12px;">
          <el-input
            v-model="addMemorialSearch"
            placeholder="搜索纪念标题"
            clearable
            style="width: 240px;"
            @input="handleAddMemorialSearch"
          >
            <i slot="prefix" class="el-input__icon el-icon-search"></i>
          </el-input>
          <el-select v-model="addMemorialTypeFilter" placeholder="类型" clearable style="width: 120px; margin-left: 8px;" @change="handleAddMemorialSearch">
            <el-option v-for="item in memorialTypeOptions" :key="item.key" :label="item.name" :value="item.key"></el-option>
          </el-select>
        </div>
        <el-table
          ref="addMemorialTable"
          v-loading="addMemorialLoading"
          :data="addMemorialList"
          stripe
          height="360"
          style="width: 100%"
          @selection-change="handleMemorialSelectionChange"
        >
          <el-table-column type="selection" width="50" align="center"></el-table-column>
          <el-table-column label="图标" width="70" align="center">
            <template slot-scope="scope">
              <el-image v-if="scope.row.icon" :src="getFullUrl(scope.row.icon)" fit="contain" style="width: 36px; height: 36px; border-radius: 4px;"></el-image>
              <i v-else class="el-icon-present" style="font-size: 24px; color: #dcdfe6;"></i>
            </template>
          </el-table-column>
          <el-table-column prop="title" label="纪念标题" min-width="140" show-overflow-tooltip></el-table-column>
          <el-table-column prop="memorialType" label="类型" width="90" align="center">
            <template slot-scope="scope">
              <el-tag size="mini" :type="getMemorialTypeTagType(scope.row.memorialType)">{{ getMemorialTypeLabel(scope.row.memorialType) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="seriesName" label="系列" width="90" align="center">
            <template slot-scope="scope">
              <el-tag size="mini" type="info">{{ getMemorialSeriesLabel(scope.row.seriesName) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="status" label="状态" width="80" align="center">
            <template slot-scope="scope">
              <el-tag size="mini" :type="scope.row.status === 'PUBLISHED' ? 'success' : 'info'">{{ scope.row.status === 'PUBLISHED' ? '已发布' : scope.row.status }}</el-tag>
            </template>
          </el-table-column>
        </el-table>
        <div style="margin-top: 12px; text-align: right;">
          <el-pagination
            @size-change="handleAddMemorialSizeChange"
            @current-change="handleAddMemorialPageChange"
            :current-page="addMemorialPage"
            :page-sizes="[10, 20, 50]"
            :page-size="addMemorialPageSize"
            :total="addMemorialTotal"
            layout="total, sizes, prev, pager, next"
          ></el-pagination>
        </div>
        <div slot="footer">
          <el-button @click="addMemorialDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleAddMemorials" :loading="addMemorialSubmitting" :disabled="selectedMemorialIds.length === 0">
            确认添加 ({{ selectedMemorialIds.length }})
          </el-button>
        </div>
      </el-dialog>

      <!-- 积分兑换弹窗 -->
      <el-dialog title="积分兑换权益" :visible.sync="exchangeDialogVisible" width="720px" :close-on-click-modal="false" append-to-body>
        <div style="margin-bottom: 12px;">
          <el-input v-model="exchangeSearch" placeholder="搜索权益名称" clearable style="width: 240px;" @input="handleExchangeSearch">
            <i slot="prefix" class="el-input__icon el-icon-search"></i>
          </el-input>
          <span style="margin-left: 16px; color: #909399; font-size: 13px;">当前积分：<strong style="color: #e6a23c;">{{ userPoints }}</strong></span>
        </div>
        <el-table :data="exchangeGoodsList" v-loading="loadingExchangeGoods" @selection-change="handleExchangeSelectionChange" style="max-height: 400px; overflow-y: auto;">
          <el-table-column type="selection" width="50" :selectable="row => row.exchangePoints <= userPoints"></el-table-column>
          <el-table-column label="图标" width="60" align="center">
            <template slot-scope="scope">
              <el-image v-if="scope.row.icon" :src="getFullUrl(scope.row.icon)" fit="cover" style="width: 32px; height: 32px; border-radius: 4px;"></el-image>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column prop="name" label="权益名称" min-width="140" show-overflow-tooltip></el-table-column>
          <el-table-column prop="rightsType" label="类型" width="80" align="center">
            <template slot-scope="scope">
              <el-tag size="mini" :type="getRightsTypeTagType(scope.row.rightsType)">{{ getRightsTypeLabel(scope.row.rightsType) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="exchangePoints" label="积分" width="80" align="center">
            <template slot-scope="scope">
              <span :style="{ color: scope.row.exchangePoints > userPoints ? '#f56c6c' : '#e6a23c', fontWeight: 600 }">{{ scope.row.exchangePoints }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="stockQuantity" label="库存" width="70" align="center">
            <template slot-scope="scope">{{ scope.row.stockQuantity === -1 ? '不限' : scope.row.stockQuantity }}</template>
          </el-table-column>
        </el-table>
        <div slot="footer">
          <el-button @click="exchangeDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleExchange" :loading="exchanging" :disabled="selectedExchangeGoods.length === 0">
            确认兑换 ({{ selectedExchangeGoods.length }})
          </el-button>
        </div>
      </el-dialog>

      <!-- 积分变动明细弹窗 -->
      <el-dialog
        title="积分变动明细"
        :visible.sync="pointsRecordDialogVisible"
        :width="pointsRecordDialogWidth"
        :close-on-click-modal="false"
        append-to-body
        custom-class="points-record-dialog"
      >
        <div class="points-record-header">
          <div class="record-user-block">
            <div class="record-user-avatar">
              <i class="el-icon-user-solid"></i>
            </div>
            <div class="record-user-meta">
              <div class="record-user-name">
                <strong>{{ user.username }}</strong>
                <span class="record-user-id">ID: {{ user.userid }}</span>
              </div>
              <div class="record-user-sub">积分变动历史记录</div>
            </div>
          </div>
          <div class="record-points-badge">
            <i class="el-icon-coin"></i>
            <div class="record-points-text">
              <span class="label">当前积分</span>
              <span class="value">{{ user.points || 0 }}</span>
            </div>
          </div>
        </div>

        <div class="points-record-table-wrapper">
          <el-table
            :data="pointsRecordData"
            v-loading="pointsRecordLoading"
            size="small"
            max-height="380"
            style="width: 100%"
            :header-cell-style="{ background: '#f8fafc', color: '#475569', fontWeight: 600 }"
            class="points-record-table"
          >
            <el-table-column prop="operationTime" label="变动时间" width="155" align="center">
              <template slot-scope="scope">
                <span class="cell-time">{{ formatDate(scope.row.operationTime) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="changeType" label="类型" width="90" align="center">
              <template slot-scope="scope">
                <span :class="['change-type-badge', scope.row.changeType === 'EARN' ? 'is-earn' : 'is-spend']">
                  <i :class="scope.row.changeType === 'EARN' ? 'el-icon-top' : 'el-icon-bottom'"></i>
                  {{ scope.row.changeType === 'EARN' ? '增加' : '扣除' }}
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="pointsAmount" label="变动数值" width="110" align="center">
              <template slot-scope="scope">
                <span :class="['points-amount', scope.row.changeType === 'EARN' ? 'is-positive' : 'is-negative']">
                  {{ scope.row.changeType === 'EARN' ? '+' : '-' }}{{ scope.row.pointsAmount }}
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="pointsBefore" label="变动前" width="80" align="center">
              <template slot-scope="scope">
                <span class="cell-plain">{{ scope.row.pointsBefore }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="pointsAfter" label="变动后" width="80" align="center">
              <template slot-scope="scope">
                <span class="cell-plain">{{ scope.row.pointsAfter }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="sourceType" label="来源" width="120" align="center">
              <template slot-scope="scope">
                <el-tag size="mini" effect="plain" type="info">{{ getSourceLabel(scope.row.sourceType) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="remark" label="备注" min-width="150" show-overflow-tooltip>
              <template slot-scope="scope">
                <span class="cell-remark">{{ scope.row.remark || '-' }}</span>
              </template>
            </el-table-column>
          </el-table>

          <div v-if="!pointsRecordLoading && pointsRecordData.length === 0" class="points-record-empty">
            <i class="el-icon-document"></i>
            <p>暂无积分变动记录</p>
          </div>
        </div>

        <div class="points-record-pagination">
          <el-pagination
            :current-page="pointsRecordPage"
            :page-size="pointsRecordLimit"
            :total="pointsRecordTotal"
            layout="total, prev, pager, next"
            @current-change="handlePointsRecordPageChange"
          ></el-pagination>
        </div>
        <div slot="footer" class="points-record-footer">
          <el-button type="primary" plain icon="el-icon-circle-close" @click="pointsRecordDialogVisible = false">关闭</el-button>
        </div>
      </el-dialog>

      <!-- 活动照片 -->
      <el-card id="photos" class="user-detail-photos detail-card" shadow="hover">
        <template slot="header">
          <div class="card-header">
            <span><i class="el-icon-picture-outline"></i> 活动照片</span>
            <div class="header-actions">
              <el-button
                type="text"
                icon="el-icon-refresh"
                :loading="loadingPhotos"
                @click="fetchActivityPhotos"
                style="margin-right: 15px;"
              >
                {{ $t('common.refresh') }}
              </el-button>
              <el-upload
                v-if="hasPermission('sys:user:update')"
                action=""
                :http-request="handlePhotoUpload"
                :show-file-list="false"
                accept="image/*"
                :before-upload="beforePhotoUpload"
              >
                <el-button type="primary" size="small" icon="el-icon-upload2" :loading="uploadingPhoto">
                  上传照片
                </el-button>
              </el-upload>
            </div>
          </div>
        </template>

        <div v-loading="loadingPhotos">
          <div v-if="activityPhotos.length === 0" class="photos-empty">
            <i class="el-icon-picture-outline" style="font-size: 40px; color: #dcdfe6;"></i>
            <p>暂无活动照片</p>
          </div>
          <div v-else class="photos-grid">
            <div v-for="(photo, index) in activityPhotos" :key="index" class="photo-item">
              <el-image
                :src="getFullUrl(photo)"
                fit="cover"
                class="photo-img"
                :preview-src-list="activityPhotos.map(p => getFullUrl(p))"
                :z-index="9999"
              />
              <div class="photo-overlay" v-if="hasPermission('sys:user:update')">
                <i class="el-icon-zoom-in" @click="previewPhoto(index)"></i>
                <i class="el-icon-delete" @click="handleDeletePhoto(photo, index)"></i>
              </div>
            </div>
          </div>
        </div>
      </el-card>

      <!-- 用户分组信息 -->
      <el-card id="groups" class="user-detail-groups detail-card" shadow="hover" v-loading="loadingGroups">
        <template slot="header">
          <div class="card-header">
            <span><i class="el-icon-menu"></i> {{ $t('userManagement.userGroups') }}</span>
          </div>
        </template>
        
        <el-table
          v-if="userGroups.length > 0"
          v-loading="loading"
          :data="userGroups"
          stripe
          height="300"
          style="width: 100%"
        >
          <el-table-column prop="name" :label="$t('userManagement.groupName')" align="center"></el-table-column>
          <el-table-column prop="description" :label="$t('userManagement.description')" align="left"></el-table-column>
          <el-table-column prop="user_ids" :label="$t('userManagement.memberCount')" align="center">
            <template slot-scope="scope">
              <el-tag size="mini" type="info" effect="plain" style="border-radius: 10px;">
                {{ scope.row.user_ids ? scope.row.user_ids.length : 0 }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="createDate" :label="$t('common.createdAt')" align="center">
            <template slot-scope="scope">
              {{ formatDate(scope.row.createDate) }}
            </template>
          </el-table-column>
          <el-table-column :label="$t('common.operation')" width="240" align="center">
            <template slot-scope="scope">
              <div class="action-buttons">
                <el-button
                  size="mini"
                  type="primary"
                  plain
                  icon="el-icon-view"
                  @click="handleViewGroup(scope.row)"
                  v-if="hasPermission('sys:usergroup:info')"
                >
                  {{ $t('common.detail') }}
                </el-button>
                <el-button
                  size="mini"
                  type="danger"
                  plain
                  icon="el-icon-delete"
                  @click="handleRemoveFromGroup(scope.row)"
                  v-if="hasPermission('sys:usergroup:update')"
                >
                  {{ $t('userManagement.removeFromGroup') }}
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>
        <el-empty v-else :description="$t('userManagement.noGroups')" :image-size="60"></el-empty>
      </el-card>

      <!-- 用户操作日志 -->
      <el-card id="logs" class="user-detail-logs detail-card" shadow="hover" v-loading="loadingLogs" v-if="isSuperAdmin">
        <template slot="header">
          <div class="card-header">
            <span><i class="el-icon-document"></i> {{ $t('userManagement.userOperationLogs') }}</span>
          </div>
        </template>
        
        <el-table
          v-if="userLogs.length > 0"
          :data="paginatedLogs"
          stripe
          height="400"
          style="width: 100%"
        >
          <el-table-column prop="id" :label="$t('common.id')" width="80" align="center"></el-table-column>
          <el-table-column prop="type" :label="$t('system.logType')" align="center">
            <template slot-scope="scope">
              <el-tag :type="getLogTypeTag(scope.row.type)" size="small">
                {{ scope.row.type }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="content" :label="$t('system.logContent')" align="left"></el-table-column>
          <el-table-column prop="ip" :label="$t('system.ipAddress')" align="center"></el-table-column>
          <el-table-column prop="created_at" :label="$t('common.createdAt')" align="center">
            <template slot-scope="scope">
              {{ formatDate(scope.row.created_at) }}
            </template>
          </el-table-column>
          <el-table-column :label="$t('common.actions')" width="100" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button
                size="mini"
                type="primary"
                plain
                icon="el-icon-view"
                @click="handleViewLogDetail(scope.row)"
              >
                {{ $t('common.detail') }}
              </el-button>
            </template>
          </el-table-column>
        </el-table>
        <el-empty v-else :description="$t('userManagement.noLogs')" :image-size="60"></el-empty>
        
        <div class="pagination-container" v-if="userLogTotal > logPageSize">
          <el-pagination
            @size-change="handleLogSizeChange"
            @current-change="handleLogPageChange"
            :current-page="logPage"
            :page-sizes="[5, 10, 20, 50]"
            :page-size="logPageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="userLogTotal"
          >
          </el-pagination>
        </div>
      </el-card>
    </div>

    <!-- 绑定设备弹窗 -->
    <el-dialog
      :title="$t('userManagement.bindDevice')"
      :visible.sync="bindDeviceDialogVisible"
      width="800px"
      :close-on-click-modal="false"
      append-to-body
    >
      <user-bind-devices
        v-if="bindDeviceDialogVisible"
        :user-id="userId"
        :exclude-device-ids="user.bound_device_ids || []"
        @success="handleBindDeviceSuccess"
        @close="bindDeviceDialogVisible = false"
      />
    </el-dialog>

    <el-dialog
      title="绑定NFC周边"
      :visible.sync="bindPeripheralDialogVisible"
      width="600px"
      :close-on-click-modal="false"
      append-to-body
    >
      <el-form ref="bindNfcForm" :model="bindNfcForm" :rules="bindNfcFormRules" label-width="100px">
        <el-form-item label="NFC卡号" prop="nfcUuid">
          <el-input v-model="bindNfcForm.nfcUuid" placeholder="请输入NFC卡UUID" clearable></el-input>
        </el-form-item>
        <el-form-item label="SKU编码" prop="sku">
          <el-input v-model="bindNfcForm.sku" placeholder="请输入SKU编码" clearable></el-input>
        </el-form-item>
        <el-form-item label="卡类型" prop="cardType">
          <el-select v-model="bindNfcForm.cardType" placeholder="请选择卡类型" style="width:100%">
            <el-option label="普通卡" value="NORMAL"></el-option>
            <el-option label="通用卡" value="UNIVERSAL"></el-option>
            <el-option label="活动卡" value="ACTIVITY"></el-option>
            <el-option label="限定卡" value="LIMITED"></el-option>
            <el-option label="礼品卡" value="GIFT"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="刷卡设备" prop="deviceCode">
          <el-input v-model="bindNfcForm.deviceCode" placeholder="刷卡设备编码（选填）" clearable></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="bindPeripheralDialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" @click="handleConfirmBindNfc" :loading="bindNfcLoading">确认绑定</el-button>
      </div>
    </el-dialog>

    <el-dialog
      :title="$t('system.logDetail')"
      :visible.sync="logDetailDialogVisible"
      width="650px"
      :close-on-click-modal="true"
      append-to-body
    >
      <el-descriptions v-if="currentLogDetail" :column="2" border size="medium">
        <el-descriptions-item :label="$t('common.id')" :span="1">
          {{ currentLogDetail.id }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('system.logType')" :span="1">
          <el-tag :type="getLogTypeTag(currentLogDetail.type)" size="small">
            {{ currentLogDetail.type }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item :label="$t('system.logContent')" :span="2">
          {{ currentLogDetail.content || currentLogDetail.operation || '-' }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('system.ipAddress')" :span="1">
          {{ currentLogDetail.ip || '-' }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('common.status')" :span="1">
          <el-tag :type="currentLogDetail.status === 1 ? 'success' : 'danger'" size="small">
            {{ currentLogDetail.status === 1 ? $t('common.success') : $t('common.fail') }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item :label="$t('common.createdAt')" :span="2">
          {{ formatDate(currentLogDetail.created_at || currentLogDetail.createDate) }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('system.operator')" :span="1" v-if="currentLogDetail.username || currentLogDetail.operatorId">
          {{ currentLogDetail.username || currentLogDetail.operatorId || '-' }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('system.targetType')" :span="1" v-if="currentLogDetail.targetType">
          {{ currentLogDetail.targetType }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('system.targetId')" :span="2" v-if="currentLogDetail.targetId">
          {{ currentLogDetail.targetId }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('system.detail')" :span="2" v-if="currentLogDetail.detail">
          <div class="log-detail-content">{{ currentLogDetail.detail }}</div>
        </el-descriptions-item>
        <el-descriptions-item :label="$t('system.userAgent')" :span="2" v-if="currentLogDetail.userAgent">
          <div class="log-detail-content">{{ currentLogDetail.userAgent }}</div>
        </el-descriptions-item>
      </el-descriptions>
      <div slot="footer">
        <el-button @click="logDetailDialogVisible = false" size="small">{{ $t('common.close') }}</el-button>
      </div>
    </el-dialog>

    <!-- 返回顶部 -->
    <transition name="backtop-fade">
      <div v-show="showBackTop" class="back-to-top" @click="scrollToTop">
        <i class="el-icon-top"></i>
        <span>顶部</span>
      </div>
    </transition>
  </div>
</template>

<script>
import { unbindDevice, updateDevice, unbindDeviceByCode, getNfcCardsByUserId, unbindNfcCard, createNfcCard } from '@/apis/device/device'
import { getDictDataByType } from '@/apis/system/dict'
import { getOperationLogs } from '@/apis/system/log'
import { getUserMemorials, addMemorialsToUser, getMemorialPage } from '@/apis/activity/memorial'
import { getOnlineRightsGoods, getUserRightsExchanges, exchangeRightsGoods } from '@/apis/activity/rights'
import { getActivityPhotos, uploadActivityPhoto, deleteActivityPhoto } from '@/apis/user/user'
import { getPointsRecordPage } from '@/apis/sys/points'
import { getDigitalCollectionByUser } from '@/apis/product/digitalCollection'
import request from '@/utils/request'
import UserBindDevices from './UserBindDevices.vue'
import { hasPermission } from '@/utils/permission'
import { getFullUrl } from '@/utils/index'
import { getLevelOptions } from '@/apis/system/level'
import { mapGetters } from 'vuex'
import dayjs from 'dayjs'

export default {
  name: 'UserDetail',
  components: {
    UserBindDevices
  },
  data() {
    return {
      loading: false,
      user: {},
      levelOptions: [],
      userDetailError: null,
      userGroups: [],
      userDevices: [],
      userLogs: [],
      userLogTotal: 0,
      logPage: 1,
      logPageSize: 10,
      logDetailDialogVisible: false,
      currentLogDetail: null,
      userAgents: [],
      loadingAgents: false,
      agentSearch: '',
      agentPage: 1,
      agentPageSize: 10,
      // 设备列表相关
      deviceSearch: '',
      deviceTypeFilter: '',
      firmwareTypes: [],
      devicePage: 1,
      devicePageSize: 10,
      loadingDevices: false,
      loadingGroups: false,
      loadingLogs: false,
      bindDeviceDialogVisible: false,
      scrollTop: 0,
      needsRefresh: true,
      userPeripherals: [],
      peripheralSearch: '',
      peripheralTypeFilter: '',
      peripheralTypes: [],
      peripheralPage: 1,
      peripheralPageSize: 10,
      loadingPeripherals: false,
      bindPeripheralDialogVisible: false,
      bindNfcForm: {
        nfcUuid: '',
        sku: '',
        cardType: 'NORMAL',
        deviceCode: ''
      },
      bindNfcFormRules: {
        nfcUuid: [{ required: true, message: '请输入NFC卡UUID', trigger: 'blur' }],
        sku: [{ required: true, message: '请输入SKU编码', trigger: 'blur' }],
        cardType: [{ required: true, message: '请选择卡类型', trigger: 'change' }]
      },
      bindNfcLoading: false,
      userMemorials: [],
      loadingMemorials: false,
      addMemorialDialogVisible: false,
      addMemorialLoading: false,
      addMemorialSubmitting: false,
      addMemorialList: [],
      addMemorialSearch: '',
      addMemorialTypeFilter: '',
      addMemorialPage: 1,
      addMemorialPageSize: 10,
      addMemorialTotal: 0,
      selectedMemorialIds: [],
      // 权益兑换相关
      userRights: [],
      loadingRights: false,
      exchangeDialogVisible: false,
      exchangeSearch: '',
      exchangeGoodsList: [],
      loadingExchangeGoods: false,
      selectedExchangeGoods: [],
      exchanging: false,
      userPoints: 0,
      rightsTypeMap: {},
      memorialTypeOptions: [],
      // 积分明细相关
      pointsRecordDialogVisible: false,
      pointsRecordLoading: false,
      pointsRecordData: [],
      pointsRecordPage: 1,
      pointsRecordLimit: 10,
      pointsRecordTotal: 0,
      sourceTypeMap: {
        'NFC_BIND': 'NFC刷卡绑定',
        'EXCHANGE': '权益兑换',
        'MANUAL_ADD': '手动增加',
        'MANUAL_DEDUCT': '手动扣除',
        'EXCHANGE_REFUND': '兑换退款',
        'EXPIRE': '积分过期'
      },
      // 活动照片相关
      activityPhotos: [],
      loadingPhotos: false,
      uploadingPhoto: false,
      // 数字藏品相关
      digitalCollections: [],
      loadingCollections: false,
      activeSeries: null,
      // 返回顶部
      showBackTop: false
    }
  },
  computed: {
    ...mapGetters(['allRoles']),
    ...mapGetters('user', ['currentUser']),
    isSuperAdmin() {
      return this.currentUser && this.currentUser.superAdmin === 1
    },
    userId() {
      return this.$route.params.id || (this.currentUser && this.currentUser.userid)
    },
    pointsRecordDialogWidth() {
      const w = window.innerWidth
      if (w < 768) return '95%'
      if (w < 1200) return '85%'
      return '880px'
    },
    userRoleName() {
      if (!this.user) return ''
      
      // 优先从 roleId 获取角色
      const roleId = this.user.roleId
      if (roleId) {
        const role = this.allRoles.find(r => String(r.id) === String(roleId))
        if (role) return role.roleName
      }
      
      // 回退到基础 role 字段
      if (this.user.role === 'admin') return this.$t('userManagement.admin')
      if (this.user.role === 'user') return this.$t('userManagement.user')
      
      return ''
    },
    getRoleTagType() {
      if (!this.user) return 'info'
      
      // 1. 优先检查是否是超级管理员
      if (this.user.superAdmin === 1) return 'danger'
      
      // 2. 检查角色的类型
      const roleId = this.user.roleId
      if (roleId) {
        const role = this.allRoles.find(r => String(r.id) === String(roleId))
        if (role) {
          if (role.roleCode === 'superAdmin') return 'danger'
          if (role.roleCode === 'admin') return 'warning'
        }
      }
      
      // 3. 兜底逻辑
      if (this.user.role === 'admin') return 'warning'
      return 'success'
    },
    // 过滤后的设备列表
    filteredDevices() {
      let result = this.userDevices

      // 搜索过滤
      if (this.deviceSearch) {
        const search = this.deviceSearch.toLowerCase()
        result = result.filter(device => 
          (device.mac_address && device.mac_address.toLowerCase().includes(search)) ||
          (device.name && device.name.toLowerCase().includes(search)) ||
          (device.model && device.model.toLowerCase().includes(search))
        )
      }

      // 类型过滤
      if (this.deviceTypeFilter) {
        result = result.filter(device => device.model === this.deviceTypeFilter)
      }

      return result
    },
    // 分页后的设备列表
    paginatedDevices() {
      const start = (this.devicePage - 1) * this.devicePageSize
      const end = start + this.devicePageSize
      return this.filteredDevices.slice(start, end)
    },
    // 过滤后的智能体列表
    filteredAgents() {
      let result = this.userAgents

      // 搜索过滤
      if (this.agentSearch) {
        const search = this.agentSearch.toLowerCase()
        result = result.filter(agent => 
          (agent.agentName && agent.agentName.toLowerCase().includes(search)) ||
          (agent.agentCode && agent.agentCode.toLowerCase().includes(search))
        )
      }

      return result
    },
    // 分页后的智能体列表
    paginatedAgents() {
      const start = (this.agentPage - 1) * this.agentPageSize
      const end = start + this.agentPageSize
      return this.filteredAgents.slice(start, end)
    },
    filteredPeripherals() {
      let result = this.userPeripherals

      if (this.peripheralSearch) {
        const search = this.peripheralSearch.toLowerCase()
        result = result.filter(peripheral => 
          (peripheral.nfcUuid && peripheral.nfcUuid.toLowerCase().includes(search)) ||
          (peripheral.sku && peripheral.sku.toLowerCase().includes(search)) ||
          (peripheral.deviceCode && peripheral.deviceCode.toLowerCase().includes(search))
        )
      }

      if (this.peripheralTypeFilter) {
        result = result.filter(peripheral => peripheral.cardType === this.peripheralTypeFilter)
      }

      return result
    },
    paginatedPeripherals() {
      const start = (this.peripheralPage - 1) * this.peripheralPageSize
      const end = start + this.peripheralPageSize
      return this.filteredPeripherals.slice(start, end)
    },
    paginatedLogs() {
      return this.userLogs
    },
    // 数字藏品按系列聚合
    collectionSeriesList() {
      const map = {}
      this.digitalCollections.forEach(item => {
        const key = item.seriesId || 'none'
        if (!map[key]) {
          map[key] = {
            seriesId: item.seriesId,
            seriesName: item.seriesName,
            coverImage: item.collectionImage,
            count: 0
          }
        }
        map[key].count++
      })
      return Object.values(map).sort((a, b) => b.count - a.count)
    },
    // 按系列筛选的藏品
    filteredCollections() {
      if (!this.activeSeries) return this.digitalCollections
      if (this.activeSeries === 'none') return this.digitalCollections.filter(c => !c.seriesId)
      return this.digitalCollections.filter(c => String(c.seriesId) === String(this.activeSeries))
    }
  },
  activated() {
    this.$nextTick(() => {
      const container = this.$el.querySelector('.user-detail-container') || this.$el
      if (container) {
        container.scrollTop = this.scrollTop
      }
    })
    
    if (this.needsRefresh) {
      this.needsRefresh = false
      this.loadUserDetail()
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      if (vm.needsRefresh) {
        vm.scrollTop = 0
        vm.$nextTick(() => {
          const container = vm.$el.querySelector('.user-detail-container') || vm.$el
          if (container) container.scrollTop = 0
        })
      }
    })
  },
  beforeRouteLeave(to, from, next) {
    const container = this.$el.querySelector('.user-detail-container') || this.$el
    if (container) {
      this.scrollTop = container.scrollTop
    }
    const isSubPage = to.path.startsWith('/device/') || to.path.startsWith('/agent/') || to.path.startsWith('/user/group/')
    this.needsRefresh = !isSubPage
    next()
  },
  created() {
    this.loadUserDetail()
    getLevelOptions().then(res => {
      this.levelOptions = res || []
    }).catch(() => {})
  },
  mounted() {
    this._scrollContainer = this.$el.querySelector('.user-detail-container') || this.$el
    this._scrollHandler = () => {
      this.showBackTop = this._scrollContainer.scrollTop > 300
    }
    this._scrollContainer.addEventListener('scroll', this._scrollHandler)
  },
  beforeDestroy() {
    if (this._scrollContainer && this._scrollHandler) {
      this._scrollContainer.removeEventListener('scroll', this._scrollHandler)
    }
  },
  methods: {
    hasPermission,
    getFullUrl,
    // 获取用户等级标签类型
    getLevelColor(level) {
      const item = this.levelOptions.find(l => l.levelCode === level)
      return item && item.levelColor ? item.levelColor : '#909399'
    },
    getLevelDisplayText(level) {
      const item = this.levelOptions.find(l => l.levelCode === level)
      return item ? item.levelCode + ' ' + item.levelName : (level || 'V0')
    },
    // 格式化余额
    formatBalance(balance) {
      if (balance === undefined || balance === null) return '0.00'
      return parseFloat(balance).toFixed(2)
    },
    // 加载用户详情
    async loadUserDetail() {
      this.loading = true
      try {
        const parallelTasks = []

        parallelTasks.push(
          this.$store.dispatch('fetchUserGroups').catch(() => {})
        )

        if (this.allRoles.length === 0) {
          parallelTasks.push(
            this.$store.dispatch('fetchRoles').catch(() => {})
          )
        }

        parallelTasks.push(
          this.$store.dispatch('fetchUserById', this.userId)
            .then(user => { this.user = user })
            .catch(error => {
              this.userDetailError = error?.code === 403
                ? this.$t('userManagement.noPermissionView')
                : this.$t('userManagement.userNotFound')
            })
        )

        await Promise.all(parallelTasks)

        if (this.userDetailError) {
          this.$message.error(this.userDetailError)
          this.$router.push('/user')
          return
        }

        if (!this.user) {
          this.$message.error(this.$t('userManagement.userNotFound'))
          this.$router.push('/user')
          return
        }

        await this.loadRelatedData()
      } catch (error) {
        this.$message.error(this.$t('userManagement.userNotFound'))
        this.$router.push('/user')
      } finally {
        this.loading = false
      }
    },

    // 加载关联数据（分组、设备、日志）
    async loadRelatedData() {
      await this.fetchFirmwareTypes()
      await this.fetchPeripheralTypes()

      // 1. 加载用户分组（带简单验证）
      this.loadingGroups = true
      try {
        this.userGroups = this.$store.getters.allUserGroups.filter(group => {
          const isMember = group.user_ids && group.user_ids.some(id => String(id) === String(this.userId))
          return isMember
        })
      } finally {
        this.loadingGroups = false
      }
      
      // 2. 加载用户设备（实现精准加载与一致性验证）
      this.loadingDevices = true
      try {
        const targetUserId = this.userId || this.$route.params.id
        console.log('Fetching devices for user:', targetUserId)
        
        if (!targetUserId) {
          console.error('No userId found for fetching devices')
          this.userDevices = []
          return
        }

        // 性能优化：直接按用户ID查询设备，解决分页导致的数据不全问题
        const allDevices = await this.$store.dispatch('fetchDevices', { 
          forceRefresh: true,
          userId: targetUserId,
          user_id: targetUserId,
          productType: 'DEVICE',
          limit: 100
        })
        
        // 由于后端已经按 userId 过滤，这里只需要进行简单的同步校验
        this.userDevices = allDevices
        
        // 如果后端显式返回了绑定设备ID列表，进行补充校验（可选）
        if (Array.isArray(this.user.bound_device_ids) && this.user.bound_device_ids.length > 0) {
          const deviceIdsInTable = new Set(this.userDevices.map(d => String(d.id)))
          this.user.bound_device_ids.forEach(id => {
            if (!deviceIdsInTable.has(String(id))) {
              console.warn(`[Consistency] User has bound_device_id ${id} but it was not returned in device list query.`)
            }
          })
        }

        // 4. 最终一致性检查：校验 deviceCount
        const dynamicCount = this.userDevices.length
        if (this.user.deviceCount !== dynamicCount) {
          console.warn(`[Consistency] User ${this.userId} deviceCount mismatch: DB says ${this.user.deviceCount}, calculated ${dynamicCount}. Correcting local state.`)
          this.user.deviceCount = dynamicCount
        }
      } catch (error) {
        console.error('Failed to load user devices:', error)
        this.$message.error(this.$t('userManagement.fetchDevicesError'))
      } finally {
        this.loadingDevices = false
      }

      await this.fetchUserPeripherals()
      
      await this.fetchUserLogs()

      // 4. 加载用户关联智能体
      await this.fetchUserAgents()

      // 5. 加载用户纪念记录
      await this.fetchUserMemorials()

      // 6. 加载数字藏品
      await this.fetchDigitalCollections()

      // 7. 加载用户权益兑换记录
      await this.fetchUserRights()

      // 8. 加载活动照片
      await this.fetchActivityPhotos()
    },

    // 获取用户智能体列表
    async fetchUserAgents() {
      this.loadingAgents = true
      try {
        // 优先使用管理员接口，支持 userId 过滤
        const res = await request({
          url: '/agent/all',
          method: 'get',
          params: { 
            userId: this.userId,
            limit: 1000
          }
        })
        this.userAgents = res.list || []
      } catch (error) {
        console.error('Failed to load user agents via /agent/all:', error)
        // 降级尝试普通用户列表接口
        try {
          const res = await request({
            url: '/agent/list',
            method: 'get',
            params: { 
              userId: this.userId
            }
          })
          this.userAgents = res || []
        } catch (err) {
          console.error('Failed to load user agents via /agent/list:', err)
          this.userAgents = []
        }
      } finally {
        this.loadingAgents = false
      }
    },

    // 智能体搜索
    handleAgentSearch() {
      this.agentPage = 1
    },

    // 获取用户纪念记录
    async fetchUserMemorials() {
      this.loadingMemorials = true
      try {
        const res = await getUserMemorials(this.userId)
        this.userMemorials = res || []
      } catch (error) {
        console.error('Failed to load user memorials:', error)
        this.userMemorials = []
      } finally {
        this.loadingMemorials = false
      }
    },

    // 纪念类型标签
    getMemorialTypeLabel(type) {
      const map = { BADGE: '勋章', STICKER: '贴纸', STAMP: '集章', MEDAL: '徽章', CERTIFICATE: '证书' }
      return map[type] || type || '-'
    },
    getMemorialTypeTagType(type) {
      const map = { BADGE: '', STICKER: 'success', STAMP: 'warning', MEDAL: 'danger', CERTIFICATE: 'info' }
      return map[type] || 'info'
    },
    getMemorialSeriesLabel(series) {
      const map = { ANNIVERSARY: '周年纪念', FESTIVAL: '节日纪念', EVENT: '活动纪念', ACHIEVEMENT: '成就纪念', LIMITED: '限定纪念' }
      return map[series] || series || '-'
    },
    getAcquireMethodLabel(method) {
      const map = { SCAN: '扫码', CHECK_IN: '打卡', REWARD: '奖励', MANUAL: '手动' }
      return map[method] || method || '-'
    },

    // 打开添加纪念记录弹窗
    openAddMemorialDialog() {
      this.addMemorialDialogVisible = true
      this.selectedMemorialIds = []
      this.addMemorialSearch = ''
      this.addMemorialTypeFilter = ''
      this.addMemorialPage = 1
      this.loadMemorialTypeOptions()
      this.loadAvailableMemorials()
    },

    // 加载纪念类型字典
    loadMemorialTypeOptions() {
      if (this.memorialTypeOptions.length > 0) return
      getDictDataByType('MEMORIAL_TYPE').then(res => {
        this.memorialTypeOptions = res || []
      }).catch(() => {
        this.memorialTypeOptions = []
      })
    },

    // 加载可选纪念列表
    loadAvailableMemorials() {
      this.addMemorialLoading = true
      const params = {
        page: this.addMemorialPage,
        limit: this.addMemorialPageSize,
        status: 'PUBLISHED',
        title: this.addMemorialSearch || undefined,
        memorialType: this.addMemorialTypeFilter || undefined
      }
      getMemorialPage(params).then(res => {
        if (res) {
          this.addMemorialList = res.list || []
          this.addMemorialTotal = res.total || 0
        }
      }).catch(e => {
        console.error('加载纪念列表失败:', e)
      }).finally(() => {
        this.addMemorialLoading = false
      })
    },

    handleAddMemorialSearch() {
      this.addMemorialPage = 1
      this.loadAvailableMemorials()
    },

    handleAddMemorialPageChange(page) {
      this.addMemorialPage = page
      this.loadAvailableMemorials()
    },

    handleAddMemorialSizeChange(size) {
      this.addMemorialPageSize = size
      this.addMemorialPage = 1
      this.loadAvailableMemorials()
    },

    handleMemorialSelectionChange(selection) {
      this.selectedMemorialIds = selection.map(item => item.id)
    },

    // 确认添加纪念记录
    async handleAddMemorials() {
      if (this.selectedMemorialIds.length === 0) return
      this.addMemorialSubmitting = true
      try {
        await addMemorialsToUser(this.userId, this.selectedMemorialIds)
        this.$message.success('添加纪念记录成功')
        this.addMemorialDialogVisible = false
        this.fetchUserMemorials()
      } catch (e) {
        console.error('添加纪念记录失败:', e)
        this.$message.error('添加纪念记录失败')
      } finally {
        this.addMemorialSubmitting = false
      }
    },

    // 智能体分页
    handleAgentPageChange(page) {
      this.agentPage = page
    },

    handleAgentSizeChange(size) {
      this.agentPageSize = size
      this.agentPage = 1
    },

    // 查看智能体详情
    handleViewAgent(row) {
      this.$router.push({
        path: `/agent/detail/${row.id}`,
        query: { redirect: this.$route.fullPath }
      })
    },

    // 查看智能体聊天记录
    handleViewChatHistory(row) {
      this.$router.push({
        path: '/agent/chat',
        query: { 
          agentId: row.id,
          userId: this.userId
        }
      })
    },

    // 删除智能体
    handleDeleteAgent(row) {
      this.$confirm(this.$t('agent.deleteConfirm'), this.$t('common.tips'), {
        confirmButtonText: this.$t('common.confirm'),
        cancelButtonText: this.$t('common.cancel'),
        type: 'warning'
      }).then(() => {
        request({
          url: `/agent/${row.id}`,
          method: 'delete'
        }).then(() => {
          this.$message.success(this.$t('common.operationSuccess'))
          this.fetchUserAgents()
        })
      }).catch(() => {})
    },
    
    // 获取固件类型字典
    async fetchFirmwareTypes() {
      try {
        const data = await getDictDataByType('FIRMWARE_TYPE')
        if (data) {
          this.firmwareTypes = data
        }
      } catch (error) {
        console.error('获取固件类型失败:', error)
      }
    },

    async fetchPeripheralTypes() {
      try {
        const data = await getDictDataByType('PERIPHERAL_TYPE')
        if (data) {
          this.peripheralTypes = data
        }
      } catch (error) {
        console.error('获取周边类型失败:', error)
      }
    },

    async fetchUserPeripherals() {
      this.loadingPeripherals = true
      try {
        const targetUserId = this.userId || this.$route.params.id
        
        if (!targetUserId) {
          console.error('No userId found for fetching peripherals')
          this.userPeripherals = []
          return
        }

        const res = await getNfcCardsByUserId(targetUserId)
        this.userPeripherals = (res && res.data) ? res.data : (res || [])
      } catch (error) {
        console.error('Failed to load user peripherals:', error)
        this.$message.error('获取NFC周边列表失败')
        this.userPeripherals = []
      } finally {
        this.loadingPeripherals = false
      }
    },

    async fetchUserLogs() {
      this.loadingLogs = true
      try {
        const logRes = await getOperationLogs({
          targetId: this.userId,
          targetType: 'user',
          page: this.logPage,
          limit: this.logPageSize
        })
        
        if (logRes && logRes.list) {
          this.userLogTotal = logRes.total || 0
          this.userLogs = logRes.list.map(log => ({
            ...log,
            user_id: log.operatorId || log.targetId,
            content: log.operation || log.content,
            type: log.type || 'user_operation',
            created_at: log.createDate
          }))
        } else {
          this.userLogs = []
        }
      } catch (error) {
        console.error('Failed to load user operation logs:', error)
        this.userLogs = []
      } finally {
        this.loadingLogs = false
      }
    },

    handleLogPageChange(page) {
      this.logPage = page
      this.fetchUserLogs()
    },

    handleLogSizeChange(size) {
      this.logPageSize = size
      this.logPage = 1
      this.fetchUserLogs()
    },

    getDeviceModelLabel(type) {
      if (!type) return type;
      const firmwareType = this.firmwareTypes.find(item => item.key === type)
      return firmwareType ? firmwareType.name : type
    },

    getPeripheralTypeLabel(type) {
      if (!type) return type;
      const peripheralType = this.peripheralTypes.find(item => item.key === type)
      return peripheralType ? peripheralType.name : type
    },

    getCardTypeLabel(cardType) {
      const map = { NORMAL: '普通卡', UNIVERSAL: '通用卡', ACTIVITY: '活动卡', LIMITED: '限定卡', GIFT: '礼品卡' }
      return map[cardType] || cardType || '未知'
    },

    getCardTypeTag(cardType) {
      const map = { NORMAL: '', UNIVERSAL: 'success', ACTIVITY: 'warning', LIMITED: 'danger', GIFT: 'info' }
      return map[cardType] || 'info'
    },

    // 设备搜索
    handleDeviceSearch() {
      this.devicePage = 1
    },

    // 设备分页
    handleDevicePageChange(page) {
      this.devicePage = page
    },

    handleDeviceSizeChange(size) {
      this.devicePageSize = size
      this.devicePage = 1
    },

    handlePeripheralSearch() {
      this.peripheralPage = 1
    },

    handlePeripheralPageChange(page) {
      this.peripheralPage = page
    },

    handlePeripheralSizeChange(size) {
      this.peripheralPageSize = size
      this.peripheralPage = 1
    },

    // 处理自动升级状态切换
    async handleAutoUpdateChange(device, value) {
      try {
        await updateDevice(device.id, {
          autoUpdate: value
        })
        this.$message.success(this.$t('device.updateDeviceSuccess'))
      } catch (error) {
        console.error('Failed to update device auto_update status:', error)
        this.$message.error(this.$t('device.updateDeviceFailed'))
        // 恢复原始状态
        device.auto_update = value === 1 ? 0 : 1
      }
    },

    // 处理备注失去焦点保存
    async handleRemarkBlur(device) {
      try {
        await updateDevice(device.id, {
          deviceRemark: device.device_remark
        })
        this.$message.success(this.$t('device.updateDeviceSuccess'))
      } catch (error) {
        console.error('Failed to update device remark:', error)
        this.$message.error(this.$t('device.updateDeviceFailed'))
      }
    },

    // 滚动到指定区域
    scrollToSection(id) {
      const element = document.getElementById(id)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },

    // 返回顶部
    scrollToTop() {
      const container = this._scrollContainer
      if (container) {
        container.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },

    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleString()
    },
    
    // 返回用户列表
    handleBack() {
      this.$router.go(-1)
    },
    
    // 复制文本
    handleCopy(text) {
      if (!text) return
      const input = document.createElement('input')
      input.setAttribute('value', text)
      document.body.appendChild(input)
      input.select()
      if (document.execCommand('copy')) {
        this.$message.success(this.$t('common.copySuccess'))
      }
      document.body.removeChild(input)
    },
    
    // 编辑用户
    handleEditUser() {
      this.$router.push(`/user/edit/${this.userId}`)
    },
    
    // 查看所有日志
    handleViewLogDetail(row) {
      this.currentLogDetail = row
      this.logDetailDialogVisible = true
    },

    getLogTypeTag(type) {
      const tagTypeMap = {
        'user_operation': 'primary',
        'security': 'danger',
        'resource': 'warning',
        'system': 'info'
      }
      return tagTypeMap[type] || 'info'
    },

    // 格式化日期时间
    formatDateTime(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.getFullYear() + '-' + 
             String(date.getMonth() + 1).padStart(2, '0') + '-' + 
             String(date.getDate()).padStart(2, '0') + ' ' +
             String(date.getHours()).padStart(2, '0') + ':' + 
             String(date.getMinutes()).padStart(2, '0') + ':' + 
             String(date.getSeconds()).padStart(2, '0')
    },

    // 查看设备详情
    handleViewDevice(row) {
      this.scrollTop = (this.$el.querySelector('.user-detail-container') || this.$el).scrollTop || 0
      this.$router.push({
        path: `/device/detail/${row.id}`,
        query: { redirect: this.$route.fullPath }
      })
    },

    handleBindPeripheral() {
      this.bindNfcForm = { nfcUuid: '', sku: '', cardType: 'NORMAL', deviceCode: '' }
      this.bindPeripheralDialogVisible = true
    },

    handleBindPeripheralSuccess() {
      this.bindPeripheralDialogVisible = false
      this.fetchUserPeripherals()
    },

    async handleConfirmBindNfc() {
      this.$refs.bindNfcForm.validate(async (valid) => {
        if (!valid) return
        this.bindNfcLoading = true
        try {
          await createNfcCard({
            nfcUuid: this.bindNfcForm.nfcUuid.trim(),
            sku: this.bindNfcForm.sku.trim(),
            cardType: this.bindNfcForm.cardType,
            deviceCode: this.bindNfcForm.deviceCode.trim() || undefined,
            userId: this.userId
          })
          this.$message.success('NFC卡绑定成功')
          this.bindPeripheralDialogVisible = false
          await this.fetchUserPeripherals()
        } catch (error) {
          console.error('NFC卡绑定失败:', error)
          const msg = (error.response && error.response.data && error.response.data.msg) || 'NFC卡绑定失败'
          this.$message.error(msg)
        } finally {
          this.bindNfcLoading = false
        }
      })
    },

    async handleUnbindPeripheral(row) {
      if (!row.canUnbind) {
        this.$message.warning('仅普通卡可解绑，通用卡/活动卡为永久去重记录')
        return
      }
      this.$confirm(
        '确定要解绑该NFC周边吗？',
        this.$t('common.confirm'), 
        {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        }
      ).then(async () => {
        try {
          this.loadingPeripherals = true
          
          await unbindNfcCard(row.nfcUuid)
          
          this.$message.success('NFC周边解绑成功')
          
          await this.fetchUserPeripherals()
        } catch (error) {
          console.error('NFC周边解绑失败:', error)
          const msg = (error.response && error.response.data && error.response.data.msg) || 'NFC周边解绑失败'
          this.$message.error(msg)
        } finally {
          this.loadingPeripherals = false
        }
      }).catch(() => {
      })
    },

    // 绑定新设备
    handleBindDevice() {
      this.bindDeviceDialogVisible = true
    },

    // 绑定设备成功回调
    handleBindDeviceSuccess() {
      this.bindDeviceDialogVisible = false
      this.loadRelatedData()
    },

    // 解绑设备
    handleUnbindDevice(row) {
      this.$confirm(
        this.$t('userManagement.confirmUnbind'), 
        this.$t('common.confirm'), 
        {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        }
      ).then(async () => {
        try {
          this.loadingDevices = true
          
          const deviceCode = row.deviceCode || row.device_code
          if (deviceCode) {
            await unbindDeviceByCode({ deviceCode, userId: this.userId })
          } else {
            await this.$store.dispatch('unbindDevice', { 
              deviceId: row.id, 
              userId: this.userId 
            })
          }
          
          this.$message.success(this.$t('device.unbindSuccess'))
          
          await this.loadRelatedData()
        } catch (error) {
          console.error('设备解绑失败:', error)
          this.$message.error(this.$t('device.unbindFailed'))
        } finally {
          this.loadingDevices = false
        }
      }).catch(() => {
      })
    },

    // 查看分组
    handleViewGroup(row) {
      this.scrollTop = (this.$el.querySelector('.user-detail-container') || this.$el).scrollTop || 0
      this.$router.push({
        path: `/user/group/detail/${row.id}`,
        query: { redirect: this.$route.fullPath }
      })
    },

    // 从分组中移除
    handleRemoveFromGroup(row) {
      this.$confirm(
        this.$t('userManagement.removeUserFromGroupConfirm', { username: this.user.name || this.user.username }),
        this.$t('common.confirm'),
        {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        }
      ).then(async () => {
        try {
          this.loadingGroups = true
          // 获取当前分组的所有用户ID
          const currentUserIds = row.user_ids || row.userIds || []
          // 过滤掉当前用户的ID
          const newUserIds = currentUserIds.filter(id => String(id) !== String(this.userId))
          
          // 调用 Vuex action 更新分组
          await this.$store.dispatch('updateUserGroup', {
            id: row.id,
            user_ids: newUserIds
          })
          
          this.$message.success(this.$t('userManagement.removeUserSuccess'))
          
          // 重新加载关联数据
          await this.loadRelatedData()
          // 同时更新用户详情（因为可能涉及分组数量统计）
          this.user = await this.$store.dispatch('fetchUserById', this.userId)
        } catch (error) {
          console.error('Failed to remove user from group:', error)
          this.$message.error(this.$t('userManagement.removeUserError'))
        } finally {
          this.loadingGroups = false
        }
      }).catch(() => {
        // 取消操作
      })
    },

    // ===== 权益兑换相关方法 =====
    async fetchUserRights() {
      this.loadingRights = true
      try {
        // 加载权益类型字典
        if (Object.keys(this.rightsTypeMap).length === 0) {
          const dictRes = await getDictDataByType('RIGHTS_TYPE')
          const data = dictRes || []
          this.rightsTypeMap = {}
          data.forEach(item => { this.rightsTypeMap[item.key] = item.name })
        }
        const res = await getUserRightsExchanges(this.userId)
        this.userRights = res || []
      } catch (e) {
        console.error('加载权益兑换记录失败:', e)
      } finally {
        this.loadingRights = false
      }
    },

    async openExchangeDialog() {
      this.exchangeDialogVisible = true
      this.exchangeSearch = ''
      this.selectedExchangeGoods = []
      this.userPoints = this.user.points || 0
      await this.loadExchangeGoods()
    },

    async loadExchangeGoods() {
      this.loadingExchangeGoods = true
      try {
        const res = await getOnlineRightsGoods()
        let list = res || []
        if (this.exchangeSearch) {
          const kw = this.exchangeSearch.toLowerCase()
          list = list.filter(item => item.name && item.name.toLowerCase().includes(kw))
        }
        this.exchangeGoodsList = list
      } catch (e) {
        console.error('加载在线权益商品失败:', e)
      } finally {
        this.loadingExchangeGoods = false
      }
    },

    handleExchangeSearch() {
      this.loadExchangeGoods()
    },

    handleExchangeSelectionChange(selection) {
      this.selectedExchangeGoods = selection
    },

    async handleExchange() {
      if (this.selectedExchangeGoods.length === 0) return
      this.exchanging = true
      try {
        for (const goods of this.selectedExchangeGoods) {
          await exchangeRightsGoods(this.userId, goods.id)
        }
        this.$message.success('兑换成功')
        this.exchangeDialogVisible = false
        this.fetchUserRights()
        // 刷新用户积分
        this.$store.dispatch('fetchUserById', this.userId).then(user => { this.user = user })
      } catch (e) {
        console.error('兑换失败:', e)
        this.$message.error(e.message || '兑换失败')
      } finally {
        this.exchanging = false
      }
    },

    handleViewPointsRecords() {
      this.pointsRecordDialogVisible = true
      this.pointsRecordPage = 1
      this.loadPointsRecords()
    },

    async loadPointsRecords() {
      this.pointsRecordLoading = true
      try {
        const res = await getPointsRecordPage({
          page: this.pointsRecordPage,
          limit: this.pointsRecordLimit,
          userId: this.user.userid
        })
        this.pointsRecordData = res?.list || []
        this.pointsRecordTotal = res?.total || 0
      } catch (e) {
        console.error('加载积分明细失败:', e)
      } finally {
        this.pointsRecordLoading = false
      }
    },

    handlePointsRecordPageChange(page) {
      this.pointsRecordPage = page
      this.loadPointsRecords()
    },

    getSourceLabel(sourceType) {
      return this.sourceTypeMap[sourceType] || sourceType || '-'
    },

    getRightsTypeLabel(val) {
      return this.rightsTypeMap[val] || val || '-'
    },

    getRightsTypeTagType(val) {
      const map = { TICKET: '', EXPERIENCE: 'success', HIDDEN: 'danger' }
      return map[val] || 'info'
    },

    getRightsStatusLabel(status) {
      const map = { UNUSED: '待使用', USED: '已使用', EXPIRED: '已过期', CANCELLED: '已取消' }
      return map[status] || status
    },

    getRightsStatusType(status) {
      const map = { UNUSED: 'warning', USED: 'success', EXPIRED: 'info', CANCELLED: 'danger' }
      return map[status] || 'info'
    },

    // 活动照片相关方法
    async fetchActivityPhotos() {
      this.loadingPhotos = true
      try {
        const res = await getActivityPhotos(this.userId)
        this.activityPhotos = res || []
      } catch (error) {
        console.error('获取活动照片失败:', error)
        this.activityPhotos = []
      } finally {
        this.loadingPhotos = false
      }
    },

    // 获取用户数字藏品
    async fetchDigitalCollections() {
      this.loadingCollections = true
      try {
        const res = await getDigitalCollectionByUser(this.userId)
        this.digitalCollections = res || []
      } catch (error) {
        console.error('获取数字藏品失败:', error)
        this.digitalCollections = []
      } finally {
        this.loadingCollections = false
      }
    },

    // 系列筛选切换
    toggleSeriesFilter(seriesId) {
      this.activeSeries = this.activeSeries === seriesId ? null : seriesId
    },

    beforePhotoUpload(file) {
      const isImage = file.type.startsWith('image/')
      const isLt5M = file.size / 1024 / 1024 < 5
      if (!isImage) {
        this.$message.error('只能上传图片文件!')
        return false
      }
      if (!isLt5M) {
        this.$message.error('照片大小不能超过5MB!')
        return false
      }
      return true
    },

    async handlePhotoUpload({ file }) {
      this.uploadingPhoto = true
      try {
        await uploadActivityPhoto(this.userId, file)
        this.$message.success('照片上传成功')
        await this.fetchActivityPhotos()
      } catch (error) {
        console.error('上传活动照片失败:', error)
        this.$message.error('上传失败')
      } finally {
        this.uploadingPhoto = false
      }
    },

    previewPhoto(index) {
      // el-image 的 preview-src-list 已自动支持预览，此方法用于额外逻辑
    },

    async handleDeletePhoto(objectKey, index) {
      try {
        await this.$confirm('确定删除该照片吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        await deleteActivityPhoto(this.userId, objectKey)
        this.$message.success('照片已删除')
        this.activityPhotos.splice(index, 1)
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除活动照片失败:', error)
          this.$message.error('删除失败')
        }
      }
    }
  }
}
</script>

<style scoped lang="scss">
.user-detail-container {
  margin: -20px;
  padding: 20px;
  height: calc(100% + 40px);
  overflow-y: auto;
  overflow-x: hidden;
  color: #303133;
  box-sizing: border-box;

  /* 隐藏滚动条 */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */

  :deep(.el-table__body-wrapper) {
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
}

.control-panel {
  flex-shrink: 0;
  margin-bottom: 20px;
  background: #fff;
  padding: 15px 20px;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
}

.control-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-info {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 600;
  color: #303133;

  i {
    font-size: 20px;
    color: #409eff;
  }
}

.user-overview-card {
  margin-bottom: 24px;
  border-radius: 16px;
  border: none;
  background: linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%);
  box-shadow: 0 4px 20px rgba(0,0,0,0.05) !important;

  :deep(.el-card__body) {
    padding: 24px;
  }
}

.user-overview-content {
  display: flex;
  align-items: center;
  padding: 15px;
}

.user-avatar-wrapper {
  margin-right: 30px;

  :deep(.el-avatar) {
    border: 4px solid #fff;
    box-shadow: 0 4px 15px rgba(64, 158, 255, 0.2);
    background: linear-gradient(45deg, #409eff, #66b1ff);
    font-size: 32px;
    font-weight: bold;
  }
}

.user-main-info {
  flex: 1;
}

.user-name-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;

  .name-text {
    font-size: 28px;
    font-weight: 700;
    color: #1f2f3d;
    letter-spacing: -0.5px;
  }
}

.user-sub-info {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #606266;
  font-size: 14px;

  .divider {
    color: #dcdfe6;
    font-weight: 300;
  }

  .copy-icon {
    margin-left: 6px;
    cursor: pointer;
    color: #909399;
    transition: color 0.3s;
    &:hover {
      color: #409eff;
    }
  }
}

.info-grid {
  margin-bottom: 24px;
  
  &.display-flex {
    display: flex;
    flex-wrap: wrap;
  }

  .flex-col {
    display: flex;
    flex-direction: column;
  }

  .h-full {
    height: 100%;
    display: flex;
    flex-direction: column;

    :deep(.el-card__body) {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center; /* 垂直居中内容 */
    }
  }
}

.detail-card {
  margin-bottom: 24px;
  border-radius: 16px;
  border: 1px solid #f0f2f5;
  overflow: hidden;

  &:hover {
    box-shadow: 0 8px 24px rgba(0,0,0,0.08) !important;
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-height: 32px;
    font-weight: 800;
    font-size: 17px;
    color: #1e293b;
    padding: 6px 0;
    border-left: 4px solid #409eff;
    padding-left: 12px;
    margin-left: -20px;

    span > i {
      margin-right: 8px;
      color: #409eff;
      font-size: 19px;
      vertical-align: middle;
    }

    .el-button {
      padding: 4px 10px;
    }
  }
}

.stat-items {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  padding: 5px 0;
  flex: 1;
  align-content: center;
}

.stat-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  padding: 16px 20px;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
  overflow: hidden;
  border: 1px solid transparent;

  /* 增强的渐变背景和光影 */
  &.devices { 
    background: linear-gradient(145deg, #f0f9ff 0%, #e0f2fe 100%); 
    border-color: rgba(14, 165, 233, 0.1);
    .stat-icon-wrapper { 
      background: #fff; 
      color: #0ea5e9; 
      box-shadow: 0 4px 10px -2px rgba(14, 165, 233, 0.2); 
    }
    .stat-value { color: #0c4a6e; }
    &::after { content: "\e6db"; font-family: "element-icons"; color: #0ea5e9; }
  }
  &.peripherals { 
    background: linear-gradient(145deg, #fff0f9 0%, #fee0ec 100%); 
    border-color: rgba(236, 72, 153, 0.1);
    .stat-icon-wrapper { 
      background: #fff; 
      color: #e90ea7; 
      box-shadow: 0 4px 10px -2px rgba(236, 72, 153, 0.2); 
    }
    .stat-value { color: #6e0c59; }
    &::after { content: "\e6db"; font-family: "element-icons"; color: #e90ea7; }
  }
  &.agents {
    background: linear-gradient(145deg, #f5f3ff 0%, #ede9fe 100%);
    border-color: rgba(139, 92, 246, 0.1);
    .stat-icon-wrapper {
      background: #fff;
      color: #8b5cf6;
      box-shadow: 0 4px 10px -2px rgba(139, 92, 246, 0.2);
    }
    .stat-value { color: #4c1d95; }
    &::after { content: "\e6f3"; font-family: "element-icons"; color: #8b5cf6; }
  }
  &.collections {
    background: linear-gradient(145deg, #f0fdfa 0%, #ccfbf1 100%);
    border-color: rgba(20, 184, 166, 0.1);
    .stat-icon-wrapper {
      background: #fff;
      color: #14b8a6;
      box-shadow: 0 4px 10px -2px rgba(20, 184, 166, 0.2);
    }
    .stat-value { color: #134e4a; }
    &::after { content: "\e705"; font-family: "element-icons"; color: #14b8a6; }
  }
  &.memorials { 
    background: linear-gradient(145deg, #fff7ed 0%, #ffedd5 100%); 
    border-color: rgba(249, 115, 22, 0.1);
    .stat-icon-wrapper { 
      background: #fff; 
      color: #f97316; 
      box-shadow: 0 4px 10px -2px rgba(249, 115, 22, 0.2); 
    }
    .stat-value { color: #7c2d12; }
    &::after { content: "\e6eb"; font-family: "element-icons"; color: #f97316; }
  }
  &.groups { 
    background: linear-gradient(145deg, #f0fdf4 0%, #dcfce7 100%); 
    border-color: rgba(16, 185, 129, 0.1);
    .stat-icon-wrapper { 
      background: #fff; 
      color: #10b981; 
      box-shadow: 0 4px 10px -2px rgba(16, 185, 129, 0.2); 
    }
    .stat-value { color: #064e3b; }
    &::after { content: "\e6e5"; font-family: "element-icons"; color: #10b981; }
  }
  &.logs {
    background: linear-gradient(145deg, #fffbeb 0%, #fef3c7 100%);
    border-color: rgba(245, 158, 11, 0.1);
    .stat-icon-wrapper {
      background: #fff;
      color: #f59e0b;
      box-shadow: 0 4px 10px -2px rgba(245, 158, 11, 0.2);
    }
    .stat-value { color: #78350f; }
    &::after { content: "\e6f1"; font-family: "element-icons"; color: #f59e0b; }
  }
  &.rights {
    background: linear-gradient(145deg, #fdf2f8 0%, #fce7f3 100%);
    border-color: rgba(236, 72, 153, 0.1);
    .stat-icon-wrapper {
      background: #fff;
      color: #ec4899;
      box-shadow: 0 4px 10px -2px rgba(236, 72, 153, 0.2);
    }
    .stat-value { color: #831843; }
    &::after { content: "\e6e0"; font-family: "element-icons"; color: #ec4899; }
  }

  &::after {
    content: "";
    position: absolute;
    right: -10px;
    bottom: -10px;
    font-size: 48px;
    opacity: 0.04;
    transform: rotate(-20deg);
    transition: all 0.5s ease;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 20px -5px rgba(0, 0, 0, 0.08);
    
    &.devices { border-color: rgba(14, 165, 233, 0.3); }
    &.peripherals { border-color: rgba(236, 72, 153, 0.3); }
    &.agents { border-color: rgba(139, 92, 246, 0.3); }
    &.collections { border-color: rgba(20, 184, 166, 0.3); }
    &.memorials { border-color: rgba(249, 115, 22, 0.3); }
    &.groups { border-color: rgba(16, 185, 129, 0.3); }
    &.logs { border-color: rgba(245, 158, 11, 0.3); }
    &.rights { border-color: rgba(236, 72, 153, 0.3); }

    &::after {
      transform: rotate(0deg) scale(1.1);
      opacity: 0.08;
      right: 2px;
      bottom: 2px;
    }

    .stat-icon-wrapper {
      transform: scale(1.1) rotate(5deg);
    }
  }

  .stat-icon-wrapper {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    margin-right: 15px;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    z-index: 1;
    border: 1.5px solid #fff;
  }

  .devices .stat-icon-wrapper {
    background: linear-gradient(135deg, #0ea5e9, #38bdf8);
    color: white;
    box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
  }

  .peripherals .stat-icon-wrapper {
    background: linear-gradient(135deg, #ec4899, #f472b6);
    color: white;
    box-shadow: 0 4px 12px rgba(236, 72, 153, 0.3);
  }

  .agents .stat-icon-wrapper {
    background: linear-gradient(135deg, #8b5cf6, #a78bfa);
    color: white;
    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);
  }

  .groups .stat-icon-wrapper {
    background: linear-gradient(135deg, #10b981, #34d399);
    color: white;
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  }

  .logs .stat-icon-wrapper {
    background: linear-gradient(135deg, #f59e0b, #fbbf24);
    color: white;
    box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
  }

  .stat-info {
    text-align: left;
    z-index: 1;
  }

  .stat-value {
    font-size: 30px;
    font-weight: 800;
    line-height: 1;
    margin-bottom: 2px;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    letter-spacing: -0.5px;
  }

  .stat-label {
    font-size: 12px;
    color: #64748b;
    font-weight: 600;
    letter-spacing: 0.2px;
    white-space: nowrap;
    opacity: 0.7;
  }
}

:deep(.el-descriptions) {
  padding: 5px;
  
  .el-descriptions__body {
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid #eef2f7;
  }

  .el-descriptions__table {
    border-collapse: separate;
    border-spacing: 0;
  }
}

:deep(.el-descriptions-item__label) {
  background: #f8fafc !important;
  font-weight: 700 !important;
  color: #64748b !important;
  width: 150px;
  font-size: 13px;
  padding: 16px 20px !important;
  border-right: 1px solid #f1f5f9 !important;
  border-bottom: 1px solid #f1f5f9 !important;
  
  i {
    margin-right: 8px;
    color: #94a3b8;
    font-size: 16px;
    vertical-align: middle;
  }
}

:deep(.el-descriptions-item__content) {
  color: #334155;
  padding: 16px 24px !important;
  font-size: 14px;
  border-bottom: 1px solid #f1f5f9 !important;
  background: #fff;
}

.highlight-text {
  font-weight: 600;
  color: #0f172a;
}

.text-secondary {
  color: #64748b;
}

.tag-group {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.role-tag-item {
  border-radius: 6px;
  font-weight: 500;
}

.group-tag-item {
  border-radius: 6px;
  background-color: #f1f5f9;
  border-color: #e2e8f0;
  color: #475569;
}

.last-login-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ip-tag {
  font-family: monospace;
  background-color: #f1f5f9;
  border: none;
  color: #64748b;
}

.link-text {
  color: #409eff;
  text-decoration: none;
  font-weight: 500;
  border-bottom: 1px solid transparent;
  transition: all 0.3s;
  &:hover {
    color: #66b1ff;
    border-bottom-color: #66b1ff;
    text-decoration: underline;
  }
}

.role-tag, .status-tag {
  border: none;
  font-weight: 600;
  padding: 0 10px;
}

.status-tag {
  box-shadow: 0 2px 6px rgba(103, 194, 58, 0.2);
  &.el-tag--info {
    box-shadow: 0 2px 6px rgba(144, 147, 153, 0.2);
  }
}

:deep(.el-table) {
  border-radius: 12px;
  overflow: hidden;

  th {
    background-color: #f8f9fb !important;
    color: #1f2f3d;
    font-weight: 700;
    height: 50px;
  }

  .el-table__row {
    transition: background-color 0.3s;
  }

  .el-table__body-wrapper {
    &::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }
    &::-webkit-scrollbar-thumb {
      background: #dbdbdb;
      border-radius: 3px;
      &:hover {
        background: #c0c0c0;
      }
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
  }
}

.action-buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
  
  .el-button {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
  }
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
  padding: 0 10px;
}

.view-more-logs {
  text-align: center;
  margin-top: 20px;
  padding: 15px 0;
  border-top: 1px dashed #ebeef5;
}

.log-detail-content {
  word-break: break-all;
  white-space: pre-wrap;
  max-height: 200px;
  overflow-y: auto;
  font-size: 13px;
  line-height: 1.6;
  color: #606266;
}

:deep(.el-empty) {
  padding: 40px 0;
  .el-empty__description {
    margin-top: 10px;
    color: #909399;
  }
}

.header-actions {
  display: flex;
  align-items: center;
}

:deep(.el-input__prefix) {
  display: flex;
  align-items: center;
}

:deep(.el-input__icon) {
  line-height: inherit !important;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 25px;
}

@media screen and (max-width: 992px) {
  .stat-items {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media screen and (max-width: 768px) {
  .user-overview-content {
    flex-direction: column;
    text-align: center;
    padding: 20px 10px;
  }
  
  .user-avatar-wrapper {
    margin-right: 0;
    margin-bottom: 20px;
  }
  
  .user-name-row {
    justify-content: center;
    flex-wrap: wrap;
    margin-bottom: 15px;
  }
  
  .user-sub-info {
    justify-content: center;
    flex-wrap: wrap;
  }
  
  .user-actions {
    margin-top: 24px;
    width: 100%;
    display: flex;
    justify-content: center;
    .el-button {
      width: 80%;
    }
  }
  
  .header-actions {
    flex-direction: column;
    gap: 10px;
    width: 100%;

    .el-input, .el-select {
      width: 100% !important;
      margin-right: 0 !important;
    }

    .el-button {
      width: 100%;
      margin-right: 0 !important;
    }
  }
}

:deep(.el-loading-mask) {
  background-color: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(2px);
}

/* 纪念记录卡片样式 */
.memorial-empty {
  text-align: center;
  padding: 40px 0;
  color: #909399;
}

.memorial-empty p {
  margin-top: 12px;
  font-size: 14px;
}

.photos-empty {
  text-align: center;
  padding: 40px 0;
  color: #909399;

  p {
    margin-top: 12px;
    font-size: 14px;
  }
}

.photos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px;
  padding: 4px 0;
}

.photo-item {
  position: relative;
  width: 100%;
  padding-bottom: 100%;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid #ebeef5;
  transition: all 0.3s;

  &:hover {
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
    border-color: #409eff;

    .photo-overlay {
      opacity: 1;
    }
  }

  .photo-img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .photo-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    opacity: 0;
    transition: opacity 0.3s;

    i {
      font-size: 22px;
      color: #fff;
      cursor: pointer;
      transition: transform 0.2s;

      &:hover {
        transform: scale(1.2);
      }
    }

    .el-icon-delete {
      color: #f56c6c;
    }
  }
}

.memorial-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  padding: 4px 0;
}

.memorial-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: #f9fafc;
  border-radius: 8px;
  border: 1px solid #ebeef5;
  transition: all 0.3s;
}

.memorial-card:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border-color: #d0d3d9;
}

.memorial-icon-placeholder {
  width: 56px;
  height: 56px;
  border-radius: 8px;
  background: linear-gradient(135deg, #e8eaed 0%, #d0d3d9 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #909399;
  font-size: 24px;
}

.memorial-card-info {
  flex: 1;
  min-width: 0;
}

.memorial-card-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.memorial-card-subtitle {
  font-size: 13px;
  color: #909399;
  margin-bottom: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.memorial-card-meta {
  margin-bottom: 6px;
}

.memorial-card-time {
  font-size: 12px;
  color: #b0b3b8;
}

.memorial-method {
  color: #909399;
}

/* 数字藏品样式 */
.collection-series-overview {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 8px;
  margin-bottom: 20px;
  border-bottom: 1px solid #ebeef5;
  padding-bottom: 16px;
}

.collection-series-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 10px;
  border: 2px solid #ebeef5;
  background: #f9fafc;
  cursor: pointer;
  transition: all 0.3s;
  min-width: 160px;
  flex-shrink: 0;

  &:hover {
    border-color: #14b8a6;
    box-shadow: 0 2px 8px rgba(20, 184, 166, 0.15);
  }

  &.active {
    border-color: #14b8a6;
    background: linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%);
    box-shadow: 0 2px 12px rgba(20, 184, 166, 0.2);
  }
}

.series-cover {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
}

.series-cover-img {
  width: 100%;
  height: 100%;
}

.series-cover-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #e8eaed 0%, #d0d3d9 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #909399;
  font-size: 20px;
}

.series-info {
  min-width: 0;
}

.series-name {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.series-count {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
}

.collection-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
}

.collection-item {
  border-radius: 10px;
  border: 1px solid #ebeef5;
  overflow: hidden;
  transition: all 0.3s;
  background: #fff;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
}

.collection-image-wrapper {
  position: relative;
  width: 100%;
  padding-top: 100%;
  background: #f5f7fa;
}

.collection-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.collection-image-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
  font-size: 36px;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8eaed 100%);
}

.collection-status-badge {
  position: absolute;
  top: 8px;
  right: 8px;
}

.collection-detail {
  padding: 10px 12px 12px;
}

.collection-name {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.collection-series-name {
  font-size: 12px;
  color: #14b8a6;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.collection-meta {
  margin-top: 6px;
}

.collection-no {
  font-size: 11px;
  color: #909399;
  font-family: 'Courier New', monospace;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  max-width: 100%;
}

.collection-time {
  font-size: 11px;
  color: #b0b3b8;
  margin-top: 4px;

  i {
    margin-right: 2px;
  }
}

/* 返回顶部按钮 */
.back-to-top {
  position: fixed;
  right: 40px;
  bottom: 40px;
  z-index: 2000;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #14b8a6 0%, #0d9488 100%);
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(20, 184, 166, 0.4);
  transition: all 0.3s ease;

  i {
    font-size: 18px;
    line-height: 1;
  }

  span {
    font-size: 10px;
    line-height: 1;
    margin-top: 2px;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(20, 184, 166, 0.5);
    background: linear-gradient(135deg, #0d9488 0%, #0f766e 100%);
  }

  &:active {
    transform: translateY(-1px);
  }
}

.backtop-fade-enter-active,
.backtop-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.backtop-fade-enter,
.backtop-fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>

<!-- 积分变动明细弹窗样式 - 非scoped以支持append-to-body -->
<style lang="scss">
.points-record-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  margin-bottom: 18px;
  background: linear-gradient(135deg, #fff8ed 0%, #fdf4e3 100%);
  border: 1px solid #f5dab1;
  border-radius: 10px;

  .record-user-block {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .record-user-avatar {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    background: linear-gradient(135deg, #409eff, #66b1ff);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      font-size: 20px;
      color: #fff;
    }
  }

  .record-user-meta {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .record-user-name {
    font-size: 15px;
    color: #303133;
    display: flex;
    align-items: center;
    gap: 8px;

    .record-user-id {
      color: #909399;
      font-size: 12px;
      font-weight: 400;
    }
  }

  .record-user-sub {
    font-size: 12px;
    color: #909399;
  }

  .record-points-badge {
    display: flex;
    align-items: center;
    gap: 10px;
    background: #fff;
    padding: 8px 16px;
    border-radius: 20px;
    box-shadow: 0 2px 8px rgba(230, 162, 60, 0.15);

    > i {
      font-size: 22px;
      color: #e6a23c;
    }

    .record-points-text {
      display: flex;
      flex-direction: column;
      line-height: 1.2;

      .label {
        font-size: 11px;
        color: #909399;
      }

      .value {
        font-size: 20px;
        font-weight: 700;
        color: #e6a23c;
      }
    }
  }
}

.points-record-table-wrapper {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #ebeef5;
}

.points-record-table {
  .el-table__header-wrapper th {
    font-size: 13px;
  }

  .el-table__body .el-table__row {
    td {
      padding: 10px 0;
      font-size: 13px;
    }

    &:nth-child(even) td {
      background-color: #fafbfc;
    }

    &:hover > td {
      background-color: #f0f7ff !important;
    }
  }
}

.cell-time {
  color: #606266;
  font-size: 12px;
  font-family: 'Courier New', monospace;
}

.change-type-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;

  i {
    font-size: 12px;
  }

  &.is-earn {
    color: #67c23a;
    background: #f0f9eb;
  }

  &.is-spend {
    color: #f56c6c;
    background: #fef0f0;
  }
}

.points-amount {
  font-size: 15px;
  font-weight: 700;

  &.is-positive {
    color: #67c23a;
  }

  &.is-negative {
    color: #f56c6c;
  }
}

.cell-plain {
  color: #606266;
  font-size: 13px;
}

.cell-remark {
  color: #909399;
  font-size: 12px;
}

.points-record-empty {
  padding: 40px 0;
  text-align: center;
  color: #c0c4cc;

  i {
    font-size: 36px;
    margin-bottom: 8px;
    display: block;
  }

  p {
    margin: 0;
    font-size: 13px;
  }
}

.points-record-pagination {
  margin-top: 16px;
  display: flex;
  justify-content: center;

  .el-pagination {
    .btn-prev, .btn-next, .el-pager li {
      background-color: #f8fafc !important;
      border-radius: 4px;
      border: 1px solid #f1f5f9;
      margin: 0 2px;

      &.active {
        background-color: #409eff !important;
        color: #fff !important;
        border-color: #409eff;
      }
    }
  }
}

.points-record-footer {
  text-align: center;

  .el-button {
    min-width: 100px;
    border-radius: 20px;
  }
}

@media screen and (max-width: 768px) {
  .points-record-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;

    .record-points-badge {
      align-self: flex-end;
    }
  }
}
</style>